import { supertypeClass, property, remote } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import PubSub from 'pubsub-js';
import { Policy } from './Policy';
import { Applicant } from './Applicant';
import { Avocation } from './Avocation';
import { AccidentAtFault } from './AccidentAtFault';
import { SpeedingTicket } from './SpeedingTicket';
import { RecklessTicket } from './RecklessTicket';
import { OtherMovingViolation } from './OtherMovingViolation';
import { DrivingWhileSuspendedCon } from './DrivingWhileSuspendedCon';
import { DUIConviction } from './DUIConviction';
import { TravelCountry } from './TravelCountry';
import { BigGameCountry } from './BigGameCountry';
import { Customer } from './Customer';
import { Utils } from '../Utils';
import { DiagnosisShort } from './DiagnosisShort';
import { OccupationFactors } from './OccupationFactors';
import { SubjectManager } from '../subjectManager/SubjectManager';
import { Product } from '../static/Product';
import { Assumptions } from '../static/Assumptions';
import {
  RiskFactors as GQLRiskFactors,
  ConditionType,
  CancerType,
  Cancer as GQLCancer,
  MedicalCondition as GQLMedicalCondition,
  SubstanceUsage as GQLSubstanceUsage,
  SubstanceType,
  MedicalTreatment as GQLMedicalTreatment,
  TreatmentType,
  MedicalPrevention as GQLMedicalPrevention,
  ResultsFindings,
  TestType,
  ConditionStatus,
  Frequency,
  TreatmentStatus,
  QualitativeResult,
  TimePeriod,
  ConditionComplications,
} from '@hit/hit-api-client';
import { MedicalCondition, MedicalConditionType } from './MedicalCondition';
import { MedicalTreatment } from './MedicalTreatment';
import { MedicalPrevention } from './MedicalPrevention';

@supertypeClass({ toClient: true, toServer: true })
export class RiskFactors extends Everything implements GQLRiskFactors {
  @property({ isLocal: true, persist: false })
  armedForcesTrigger;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Becoming or currently in armed forces',
  })
  armedForces: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Have you had military service deferment, rejection, or discharge because of a physical or mental condition',
  })
  medicalDischarge: boolean;

  /**
   * deprecated - incorrectly typed property for 2018 filing
   */
  @property({
    rule: ['required'],
    comment:
      'diagnosis details of military service deferment, rejection, or discharge because of a physical or mental condition',
  })
  medicalDischargeDetails: DiagnosisShort;

  @property({
    rule: ['required'],
    comment:
      'diagnosis details of military service deferment, rejection, or discharge because of a physical or mental condition',
  })
  medicalDischargeDetailsStr: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you ever been convicted of a felony, or currently on parole or probation',
  })
  isFelonios: boolean;

  @property({
    rule: ['required'],
    values: ['felony', 'misdemeanor'],
    descriptions: { felony: 'Felony', misdemeanor: 'Misdemeanor' },
    comment: 'More details, reflexive to isFelonious',
  })
  chargeType: string;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
    comment: 'What was the date of the felony or misdemeanor',
  })
  chargeDate: Date;

  @property({
    rule: ['required'],
    comment: 'County and state of charges or conviction',
  })
  chargeLocationDetails: string;

  @property({
    rule: ['required'],
    values: ['jail', 'probation', 'none'],
    descriptions: { jail: 'Jail or prison time', probation: 'Probation', none: 'None of these' },
  })
  sentenceType: string;

  @property({
    rule: ['required'],
    comment: 'Are you currently on parole or probation?',
  })
  probationNow: boolean;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
    comment: 'When did your probation or parole end?',
  })
  probationEndDate: Date;

  @property({
    rule: ['required'],
  })
  jailPrisonSentenceLength: number;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
  })
  jailReleaseDate: string;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
  })
  jailPrisonReleaseDate: Date;

  @property({ rule: ['required'], comment: 'Details, including dates and nature of felony conviction' })
  isFeloniosDetails: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 3 years, or within the next 2 years, have you been or do you expect to become a pilot, a student pilot or crew member of any aircraft, including private pilot',
  })
  isPilot: boolean;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
    comment: 'What was the date of your last flight other than as a passenger',
  })
  lastFlightDate: Date;

  @property({
    rule: ['required'],
    values: ['pilot', 'crew'],
    descriptions: { pilot: 'Pilot or Co-Pilot', crew: 'Crew Member' },
    comment: 'Pilot type',
  })
  pilot: string;

  @property({
    rule: ['required'],
    values: ['Private_Student', 'Commercial', 'Military'],
    descriptions: {
      Private_Student: 'Private or Student (not flying for hire)',
      Commercial: 'Commercial (flying for hire)',
      Military: 'Military (including Reserve and National Guard)',
    },
    comment: 'Pilot category',
  })
  pilotCategory: string;

  @property({ rule: ['required', 'numericPositive'], comment: 'Total pilot hours logged' })
  pilotTotalHrs: number;

  @property({ rule: ['required', 'numericPositive'], comment: 'Hours logged in last 12 month' })
  pilotLast12MonthsHrs: number;

  @property({ rule: ['required', 'numericPositive'], comment: 'Estimated hours logged within next 12 months' })
  pilotNext12MonthsHrs: number;

  @property({
    rule: ['required', 'numericPositive'],
    comment: 'How many hours did you or do you plan to log as a pilot',
  })
  pilotTotalHrsPastFuture: number;

  @property({ rule: ['required'], comment: 'What is your title as a crew member' })
  crewMemberTitle: string;

  @property({ rule: ['required', 'numericPositive'], comment: 'Hours logged in last 12 months as a crew member' })
  crewMemberLast12MonthsHrs: number;

  @property({ rule: ['required', 'numericPositive'], comment: 'Estimate hours for next 12 months as a crew member' })
  crewMemberNext12MonthsHrs: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you flown or within the next two years do you expect to fly outside the continental United States',
  })
  flyWithinUS: boolean;

  @property({ rule: ['required'] })
  flyOutsideUSDetails: string;

  @property({
    rule: ['required'],
    values: ['None', 'Student', 'Private', 'Commercial', 'Flight_Instructor', 'Air_Trasport_Pilot'],
    descriptions: {
      None: 'None',
      Student: 'Student',
      Private: 'Private',
      Commercial: 'Commercial',
      Flight_Instructor: 'Flight Instructor',
      Air_Trasport_Pilot: 'Air Transport Pilot',
    },
    comment: 'What type of pilot license/certificate do you hold',
  })
  licenseType: string;

  @property({
    rule: ['required'],
    values: ['Visual_flight_rating', 'instrument_flight_rating', 'other'],
    descriptions: {
      Visual_flight_rating: 'Visual Flight Rating',
      instrument_flight_rating: 'Instrument Flight Rating',
      other: 'Other',
    },
    comment: 'Flight rating type',
  })
  ratingType: string;

  @property({ rule: ['required'], comment: 'Flight rating type (if other)' })
  ratingOther: string;

  @property({ rule: ['required'], comment: 'Make and model of airplane owned/flown' })
  flyMakeModel: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you ever fly solo',
  })
  flySolo: boolean;

  @property({
    rule: ['required'],
    comment: 'Do you ever fly solo, detail',
  })
  flySoloDetails: string;

  @property({ rule: ['required', 'numericPositive'], comment: 'Total pilot hours logged' })
  flySoloTotalHrs: number;

  @property({
    rule: ['required'],
    values: ['solo', 'both'],
    descriptions: { solo: 'Solo', both: 'Both pilot and co-pilot aboard' },
  })
  flyType: string;

  @property({ comment: 'Pleasure or Personal Business' })
  flyTypePersonal: boolean;

  @property({ comment: 'Air Taxi' })
  flyTypeAirTaxi: boolean;

  @property({ comment: 'Test - Experimental' })
  flyTypeTest: boolean;

  @property({ comment: 'Scheduled Airlines (listed by FAA as Certified Route Air Carriers)' })
  flyTypeScheduled: boolean;

  @property({ comment: 'Company Business' })
  flyTypeCompany: boolean;

  @property({
    rule: ['required'],
    values: ['Hire', 'Not_Hire'],
    descriptions: { Hire: 'Hire', Not_Hire: 'Not for Hire' },
  })
  flyTypeHire: string;

  @property()
  flyTypeCharters: boolean;

  @property({ comment: 'Test - Production Line' })
  flyTypeProd: boolean;

  @property({ comment: 'Cargo Carriers' })
  flyTypeCargo: boolean;

  @property({ comment: 'Flight Instructor' })
  flyTypeInstructor: boolean;

  @property({ comment: 'Supplemental Air Carriers' })
  flyTypeSupplemental: boolean;

  @property({ comment: 'Dusting, Seeding and Spraying' })
  flyTypeDusting: boolean;

  @property({ comment: 'Seeding and Spraying' })
  flyTypeSeeding: boolean;

  @property({ comment: 'Other Airlines (non-scheduled passenger service)' })
  flyTypeOtherAirlines: boolean;

  @property({ comment: 'Fly type - Other(not listed)' })
  flyTypeOther: boolean;

  @property({ rule: ['required'], comment: 'Other fly type details' })
  flyTypeOtherStr: string;

  @property({ comment: 'Single Engine' })
  flyAircraftTypeSingle: boolean;

  @property()
  flyAircraftTypePropeller: boolean;

  @property()
  flyAircraftTypeSingleHelicopter: boolean;

  @property()
  flyAircraftTypeSingleSailplane: boolean;

  @property({ comment: 'Hot Air Balloon' })
  flyAircraftTypeHotAirBallon: boolean;

  @property({ comment: 'Gas or Helium' })
  flyAircraftTypeGas: boolean;

  @property({ comment: 'Multi-Engine' })
  flyAircraftTypeMulti: boolean;

  @property()
  flyAircraftTypeJet: boolean;

  @property({ comment: 'Bomber' })
  flyAircraftTypeBomber: boolean;

  @property({ comment: 'Fighter or Interceptor' })
  flyAircraftTypeFighter: boolean;

  @property({ comment: 'Transport' })
  flyAircraftTypeTransport: boolean;

  @property({ comment: 'has ever flown Jet in the past 12 months' })
  flyAircraftUltralightSoarding: boolean;

  @property()
  flyAircraftTypeGlider: boolean;

  @property()
  flyAircraftTypeHomeBuilt: boolean;

  @property({ rule: ['required'] })
  flyAircraftTypeHomeBuiltDetails: string;

  @property({
    comment: 'Reconnaissance or Liaison',
  })
  flyAircraftTypeReconOrLiaison: boolean;

  @property({
    comment: 'Test/Experimental',
  })
  flyAircraftTypeExperimental: boolean;

  @property({ comment: 'Other type of aircrafts(not listed)' })
  flyAircraftTypeOther: boolean;

  @property({ rule: ['required'], comment: 'Other type of aircrafts details' })
  flyAircraftTypeOtherDetails: string;

  @property({
    rule: ['required'],
    values: ['airforce', 'army', 'navy', 'marine', 'coast_guard'],
    descriptions: {
      airforce: 'Air Force',
      army: 'Army',
      navy: 'Navy',
      marine: 'Marine Corps',
      coast_guard: 'Coast Guard',
    },
    comment: 'What is your Military Organization or Branch of Service',
  })
  militaryServiceBranch: string;

  @property({
    rule: ['required'],
    values: ['e1e4', 'e5', 'officer', 'officer5'],
    descriptions: {
      e1e4: 'Enlisted (E1-E4)',
      e5: 'Enlisted (E5 & up)',
      officer: 'Officer (01-04)',
      officer5: 'Officer (05 & up)',
    },
    comment: 'What is your Rank or Grade',
  })
  militaryRank: string;

  @property({
    rule: ['required'],
    values: [
      'student_pilot',
      'instructor',
      'mac',
      'transport',
      'surgeon',
      'reserve',
      'fighter',
      'proficiency',
      'other',
    ],
    descriptions: {
      student_pilot: 'Student Pilot',
      instructor: 'Instructor',
      mac: 'M.A.C.',
      transport: 'Transport',
      surgeon: 'Flight Surgeon/Nurse',
      reserve: 'Reserve/National Guard',
      fighter: 'Fighter Pilot',
      proficiency: 'Proficiency Flying',
      other: 'Other',
    },
    comment: 'Indicate your current Military Assignment/Duties:',
  })
  militaryDuties: string;

  @property({ rule: ['required'], comment: 'Current other Military Assignment/Duties' })
  militaryDutiesOther: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you flown or do you expect to fly within the next two years, in any military carrier-based aircraft',
  })
  militaryFlyCarrier: boolean;

  @property({ comment: 'Military carrier-based aircraft details' })
  militaryFlyCarrierDetails: string;

  @property({ comment: 'Single Engine' })
  militaryFlyAircraftTypeSingle: boolean;

  @property()
  militaryFlyAircraftTypePropeller: boolean;

  @property()
  militaryFlyAircraftTypeHelicopter: boolean;

  @property({ comment: 'Reconnaissance or Liaison' })
  militaryFlyAircraftTypeRecon: boolean;

  @property({ comment: 'Test/Experimental' })
  militaryFlyAircraftTypeTest: boolean;

  @property({ rule: ['required'] })
  militaryFlyAircraftTypeTestDetails: string;

  @property({ comment: 'Multi-Engine' })
  militaryFlyAircraftTypeMulti: boolean;

  @property()
  militaryFlyAircraftTypeJet: boolean;

  @property()
  militaryFlyAircraftTypeBomber: boolean;

  @property({ comment: 'Fighter or Interceptor' })
  militaryFlyAircraftTypeFighter: boolean;

  @property({ comment: 'has flown as a pilot with ultralight or soaring' })
  militaryFlyAircraftTypeUltralightSoaring: boolean;

  @property()
  militaryFlyAircraftTypeTransport: boolean;

  @property({ comment: 'Other type of military aircraft' })
  militaryFlyAircraftTypeOther: boolean;

  @property({ rule: ['required'], comment: 'Other type of military aircraft details' })
  militaryFlyAircraftTypeOtherDetails: string;

  @property({
    rule: ['required'],
    values: ['current_member', 'National_Guard', 'ROTC', 'plan_enlist', 'Other'],
    descriptions: {
      current_member: 'Current member',
      ROTC: 'ROTC/Academy',
      plan_enlist: 'Plan to Enlist',
      Other: 'Other',
    },
    comment: 'Military service category',
  })
  militaryCapacity: string;

  @property({ rule: ['required'], comment: 'Military service details' })
  militaryDetails: string;

  @property({ rule: ['required', 'date'], comment: 'Military service discharge date' })
  militaryDischargeDate: Date;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you receiving or do you expect to receive hazardous duty or incentive pay',
  })
  militaryHazardPay: boolean;

  @property({ rule: ['required'], comment: 'Detais of military hazardous duty or incentive pay' })
  militaryHazardPayDetails: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Have you received orders for duty outside the United States',
  })
  militaryOutsideUS: boolean;

  @property({ rule: ['required'], comment: 'Details of military duty outside the United States' })
  militaryOutsideUSDetails: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 3 years or within the next 2 years, have you taken or do you intend to take part in underwater diving, hang gliding, parasailing, parakiting, parachuting, skydiving, ultralight, soaring, ballooning, bungee jumping, rock or mountain climbing, helicopter skiing, or organized racing by automobile, motorcycle, motorboat or snowmobile',
  })
  hasAvocations: boolean;

  @property({ comment: 'Auto racing' })
  avAutoRacing: boolean;

  @property({ comment: 'All Terrain Vehicles (ATV), excluding Three-Wheel' })
  avARATV: boolean;

  @property({ comment: 'Three-Wheel All Terrain Vehicles (ATV)' })
  avARATV3Wheel: boolean;

  @property({ comment: 'Auto racing: Auto crash' })
  avARCrash: boolean;

  @property({ comment: 'Dive Bomber, Rollovers, "T" Bone' })
  avARCDiveBomber: boolean;

  @property({ comment: 'Demolition, Destruction Derby, Figure "8" Demolition' })
  avARCDemolition: boolean;

  @property({ comment: 'Auto racing: Auto crash - Other/Not Sure' })
  avARCOther: boolean;

  @property({ comment: 'Kart Racers (Go-Cart)' })
  avARGoCart: boolean;

  @property({ comment: 'Sprint car racing' })
  avARGCSprint: boolean;

  @property({ comment: 'Enduro car racing' })
  avARGCEnduro: boolean;

  @property({ comment: 'Formula Kart Experiment (FKE)' })
  avARGCFormula: boolean;

  @property({ comment: 'Midget (other than Full Size)' })
  avARGCMidget: boolean;

  @property({ comment: 'Mini car racing' })
  avARGCMini: boolean;

  @property({ comment: 'Off road car racing' })
  avAROffRoad: boolean;

  @property({ comment: 'Desert (incl. Baja, Dune, Trail) - U.S. Only' })
  avARORDesert: boolean;

  @property({ comment: 'Long Distance, Rally - U.S. Only' })
  avARORRally: boolean;

  @property({ comment: 'Off road car racing - Other or Outside U.S.' })
  avAROROther: boolean;

  @property({ comment: 'Details about Off road car racing - Other or Outside U.S.' })
  avAROROtherDetails: string;

  @property({ comment: 'Rally Racing (Local Events Only)' })
  avARRally: boolean;

  @property({ comment: 'Auto racing - Other/Not Sure' })
  avAROther: boolean;

  @property({ rule: ['required'], validate: 'isNotEmptyAndMinLength(2)', comment: 'Auto racing - Other Details' })
  avAROtherDetails: string;

  @property({ comment: 'Recreational Ballooning' })
  avBalloning: boolean;

  @property({ comment: 'Big game hunting' })
  avBigGame: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: "Is all of the insured's big game hunting done in the US?",
  })
  avIsBigGameWithinUS: string;

  @property({
    type: Array,
    getType: () => BigGameCountry,
    comment: 'In which countries does the insured hunt big game?',
  })
  avBigGameCountries: Array<BigGameCountry> = [];

  @property({
    comment: 'What kind of animals does the insured hunt?',
  })
  avBigGameType: string;

  @property({ comment: 'Bobsledding (Competitive or Professional)' })
  avBobsledding: boolean;

  @property({ comment: 'Boxing (Amateur Only)' })
  avBoxing: boolean;

  @property({ comment: 'Boxing (Professional)' })
  avBoxingPro: boolean;

  @property({ comment: 'Bungee Jumping' })
  avBungee: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you plan on bungee jumping within the next two years',
  })
  avBungeeNow: boolean;

  @property({
    rule: ['required', 'numeric'],
  })
  avBungeePast: number;

  @property({
    rule: ['required', 'numeric'],
  })
  avBungeeFuture: number;

  @property({ comment: 'Hang gliding' })
  avHangGliding: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment:
      'Is your hang gliding limited to amateur, club affiliated jumps in minimal danger areas less than 500 feet altitude with no soaring',
  })
  avHangGlidingLow: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you hang glide fewer than 25 times per year',
  })
  avHangGlidingOften: boolean;

  @property({ comment: 'Heli skiing' })
  avHeliSkiing: boolean;

  @property({ comment: 'Martial Arts (Amateur or Instructor)' })
  avMartialArts: boolean;

  @property({ comment: 'Martial Arts (Professional Competition)' })
  avMartialArtsPro: boolean;

  @property({ comment: 'has participated in snowmobile racing' })
  avSnowmobileRacing: boolean;

  @property({ comment: 'Motorboat / powerboat racing' })
  avMotorboatRacing: boolean;

  @property({ comment: 'Motorcycle racing' })
  avMotorcycleRacing: boolean;

  @property({
    rule: ['required'],
    values: ['Upto125cconly', 'Upto250cconly', 'Morethan250cc'],
    descriptions: {
      Upto125cconly: 'Up to 125cc only',
      Upto250cconly: 'Up to 250cc only',
      Morethan250cc: 'More than 250cc',
    },
    comment: 'What kind of motorcycles do you race',
  })
  avMRType: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you race Grand Prix up to 250cc',
  })
  avMRGrandPrix: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Do you participate in drag racing, dragster division racing, road racing, speedway racing, or tourist trophy racing, or do you ever race outside the US',
  })
  avMRDrag: boolean;

  @property({
    rule: ['required'],
  })
  avMRDragDetails: string;

  @property({ comment: 'Mountain climbing' })
  avMountainClimbing: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are all your climbs limited to difficulty levels of Class 1-4 of the Yosemite Decimal System (YDS)',
  })
  avMCRated: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are all your climbs above class 4 limited to a rock- climbing gym',
  })
  avMCRatedGym: boolean;

  @property({ comment: 'What is your current maximum Yosemite Decimal System (YDS) class' })
  avMCYDS: string;

  @property({
    rule: ['numericPositive'],
    comment: 'How many trips to climb outdoors do you have planned in the next year',
  })
  avMCNextYearTripNumber: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Will you be doing any overnight climbs',
  })
  avMCOvernight: boolean;

  @property({ comment: 'What is the maximum elevation you will climb' })
  avMCMaxElevation: string;

  @property({ comment: 'Scuba Diving' })
  avScubaDiving: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are your dives limited to 100 feet in depth',
  })
  avSDShallow: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your certification limited to a resort or basic certification',
  })
  avSDBasicCert: boolean;

  @property({ comment: 'What diving certification(s) do you hold' })
  avSDCert: string;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you always dive with a buddy',
  })
  avSDBuddy: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you dive fewer than 50 times per year',
  })
  avSDInfrequent: boolean;

  @property({ comment: 'Skydiving, ultralight, etc' })
  avSkyDiving: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you plan to skydive within the next two years',
  })
  avSDSoon: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Do you jump fewer than 25 times per year',
  })
  avSKDInfrequent: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Is all your skydiving limited to Amateur, USPA, no baton Passing, no delayed chute openings',
  })
  avSDAmateur: string;

  @property({ rule: ['required'], comment: 'Skydiving amateur details' })
  avSDAmateurDetails: string;

  @property({ comment: 'has ever participated in Ultralight or soaring sports' })
  avUltralightSoaring: boolean;

  @property({ comment: 'Other avocations' })
  avOther: boolean;

  @property({
    comment:
      'Participating in Airborne Sports: hang gliding, parasailing, parakiting, parachuting, skydiving, ultralight, soaring, ballooning, or bungee jumping',
  })
  airborneSports: boolean;

  @property({ comment: 'Mountain Sports: rock or mountain climbing or heli skiing' })
  mountainSports: boolean;

  @property({ comment: 'Participates in organized Racing: automobile, motorcycle, motorboat or snowmobile' })
  racings: boolean;

  @property({ ccomment: 'Martial Arts: boxing, mixed martial arts or professional martial arts' })
  martialArts: boolean;

  @property({ comment: 'Underwater diving or big game hunting' })
  underwaterDiving: boolean;

  @property({ comment: 'Participating in none of the predefined sport activities' })
  noSports: boolean;

  @property({ type: Array, getType: () => Avocation })
  avOtherDetail: Array<Avocation> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
  })
  hadCancer: boolean;

  @property({ type: Array, getType: () => MedicalCondition })
  medicalConditions: MedicalCondition[] = [];

  //Bladder Cancer
  get bladderCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.BLADDER);
  }
  get isBladderCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.BLADDER);
  }
  set isBladderCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.bladderCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.BLADDER,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Breast Cancer
  get breastCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.BREAST);
  }
  get isBreastCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.BREAST);
  }
  set isBreastCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.breastCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.BREAST,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Brain Cancer
  get brainCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.BRAIN);
  }
  get isBrainCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.BRAIN);
  }
  set isBrainCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.brainCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.BRAIN,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Cervical Cancer
  get cervicalCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.CERVICAL);
  }
  get isCervicalCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.CERVICAL);
  }
  set isCervicalCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.cervicalCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.CERVICAL,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Colon Cancer
  get colonCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.COLON);
  }
  get isColonCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.COLON);
  }
  set isColonCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.colonCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.COLON,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Leukemia Cancer
  get leukemiaCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.LEUKEMIA);
  }
  get isLeukemiaCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.LEUKEMIA);
  }
  set isLeukemiaCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.leukemiaCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.LEUKEMIA,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Lymphoma
  get lymphomaCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.LYMPHOMA);
  }
  get isLymphomaCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.LYMPHOMA);
  }
  set isLymphomaCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.lymphomaCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.LYMPHOMA,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Lung Cancer
  get lungCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.LUNG);
  }
  get isLungCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.LUNG);
  }
  set isLungCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.lungCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.LUNG,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Ovarian Cancer
  get ovarianCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.OVARIAN);
  }
  get isOvarianCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.OVARIAN);
  }
  set isOvarianCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.ovarianCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.OVARIAN,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Pancreatic Cancer
  get pancreaticCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.PANCREATIC);
  }
  get isPancreaticCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.PANCREATIC);
  }
  set isPancreaticCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.pancreaticCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.PANCREATIC,
      this.hadCancer,
      hasDiagnosis
    );
  }

  //Parostate Cancer
  get prostateCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.PROSTATE);
  }
  get isProstateCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.PROSTATE);
  }
  set isProstateCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.prostateCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.PROSTATE,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // KidneyRenal
  get kidneyRenalCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.KIDNEY_RENAL);
  }
  get isKidneyRenalCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.KIDNEY_RENAL);
  }
  set isKidneyRenalCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.kidneyRenalCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.KIDNEY_RENAL,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Skin Melanoma
  get skinMelanomaCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.SKIN_MELANOMA);
  }
  get isSkinMelanomaCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.SKIN_MELANOMA);
  }
  set isSkinMelanomaCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.skinMelanomaCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.SKIN_MELANOMA,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Skin Other Cancer
  get skinOtherCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.SKIN_OTHER);
  }
  get isSkinOtherCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.SKIN_OTHER);
  }
  set isSkinOtherCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.skinOtherCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.SKIN_OTHER,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Testicular
  get testicularCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.TESTICULAR);
  }
  get isTesticularCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.TESTICULAR);
  }
  set isTesticularCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.testicularCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.TESTICULAR,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Thyroid
  get thyroidCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.THYROID);
  }
  get isThyroidCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.THYROID);
  }
  set isThyroidCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.thyroidCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.THYROID,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Other
  get otherCancer(): MedicalCondition {
    return this.getMedicalConditionByType(CancerType.OTHER);
  }
  get isOtherCancer(): boolean {
    return this.medicalConditionExistsByType(CancerType.OTHER);
  }
  set isOtherCancer(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.otherCancer,
      ConditionType.OLI_MEDCOND_CANCER,
      CancerType.OTHER,
      this.hadCancer,
      hasDiagnosis
    );
  }

  // Abnormal heart beat / arterial fib
  get abnormalHeartBeat(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_ABNORMALHEARTBEAT);
  }
  get hasAbnormalHeartBeat(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_ABNORMALHEARTBEAT);
  }
  set hasAbnormalHeartBeat(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.abnormalHeartBeat,
      ConditionType.MM_MEDCOND_ABNORMALHEARTBEAT,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis
    );
    if (!hasDiagnosis) {
      this.setMedicalCondition(this.arterialFib, ConditionType.OLI_MEDCOND_I48, undefined, false, false, undefined);
    }
  }

  // Received pulmonologist care for asthma
  get pulmonologistCareForAsthma(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_J45);
  }

  @property({
    rule: ['required'],
  })
  hadPulmonologistCareForAsthma: string;

  hadPulmonologistCareForAsthmaTrigger() {
    this.setMedicalConditionAndTreatment(
      this.pulmonologistCareForAsthma,
      ConditionType.OLI_MEDCOND_J45,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRDAsthma,
      this.hadPulmonologistCareForAsthma === 'Yes',
      TreatmentType.MM_TREATTYPE_SPECIALIST
    );
    if (this.hadPulmonologistCareForAsthma === 'Yes') {
      this.pulmonologistCareForAsthma.medicalTreatment.treatmentDesc = 'Pulmonologist';
    }
    this.resetMedicalConditionRecords();
  }

  // Eye conditions predisposing to blindness
  get eyeConditionPredisposingBlindness(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_EYECONDITIONPREDISPOSINGBLINDNESS);
  }
  @property({
    isLocal: true,
  })
  get hasEyeConditionPredisposingBlindness(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_EYECONDITIONPREDISPOSINGBLINDNESS);
  }
  set hasEyeConditionPredisposingBlindness(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.eyeConditionPredisposingBlindness,
      ConditionType.MM_MEDCOND_EYECONDITIONPREDISPOSINGBLINDNESS,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEENT,
      hasDiagnosis
    );
  }
  hasEyeConditionPredisposingBlindnessTrigger() {
    this.resetMedicalConditionRecords();
  }

  // Speech delay or other developmental issues
  get speechDelayOrDevelopmentalIssue(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_PREGWCOMP);
  }
  @property({
    isLocal: true,
  })
  get hasSpeechDelayOrDevelopmentalIssue(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_PREGWCOMP);
  }
  set hasSpeechDelayOrDevelopmentalIssue(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.speechDelayOrDevelopmentalIssue,
      ConditionType.OLI_MEDCOND_PREGWCOMP,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadComplicatedPregnancy,
      hasDiagnosis
    );
    if (hasDiagnosis) {
      this.speechDelayOrDevelopmentalIssue.conditionComplications =
        ConditionComplications.SPEECH_DELAY_OR_DEVELOPMENTAL_ISSUE;
    }
  }
  hasSpeechDelayOrDevelopmentalIssueTrigger() {
    this.resetMedicalConditionRecords();
  }

  // Abnormal heart beat / arterial fib
  get arterialFib(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_I48);
  }
  get hasArterialFib(): boolean {
    const cond = this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_I48);
    return cond && cond.optionSelected;
  }
  set hasArterialFib(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.arterialFib,
      ConditionType.OLI_MEDCOND_I48,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis,
      undefined,
      true
    );
  }

  //Coronary Artery
  get coronaryArteryDisease(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_CAD);
  }
  get hadCoronaryArteryDisease(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_CAD);
  }
  set hadCoronaryArteryDisease(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.coronaryArteryDisease,
      ConditionType.OLI_MEDCOND_CAD,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis
    );
  }

  //Myocarditis
  get myocarditis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_I40);
  }
  get hadMyocarditis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_I40);
  }
  set hadMyocarditis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.myocarditis,
      ConditionType.OLI_MEDCOND_I40,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis
    );
  }

  //Transient Ischemic Attack
  get transientIschemicAttack(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_G45);
  }
  get hadTransientIschemicAttack(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_G45);
  }
  set hadTransientIschemicAttack(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.transientIschemicAttack,
      ConditionType.OLI_MEDCOND_G45,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis
    );
  }

  //Stroke
  get stroke(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_STROKE);
  }
  get hadStroke(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_STROKE);
  }
  set hadStroke(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.stroke,
      ConditionType.OLI_MEDCOND_STROKE,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis
    );
  }

  //Other cardiovascular
  get otherCardiovascular(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_CARDIOVASCULAROTHER);
  }
  get hasOtherCardiovascular(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_CARDIOVASCULAROTHER);
  }
  set hasOtherCardiovascular(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherCardiovascular,
      ConditionType.MM_MEDCOND_CARDIOVASCULAROTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadHeartCondition,
      hasDiagnosis
    );
  }

  //Thrombocytopenia
  get thrombocytopenia(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_THROMBOCYTOPENIA);
  }
  get hadThrombocytopenia(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_THROMBOCYTOPENIA);
  }
  set hadThrombocytopenia(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.thrombocytopenia,
      ConditionType.OLI_MEDCOND_THROMBOCYTOPENIA,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBloodDisorder,
      hasDiagnosis
    );
  }

  //Immune def Syndrome
  get immuneDefSynd(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_IMMUNODEFICIENCY);
  }
  get hadImmuneDefSynd(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_IMMUNODEFICIENCY);
  }
  set hadImmuneDefSynd(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.immuneDefSynd,
      ConditionType.MM_MEDCOND_IMMUNODEFICIENCY,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBloodDisorder,
      hasDiagnosis
    );
  }

  //Deep Vein Thrombosis
  get deepVeinThrombosis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_I26);
  }
  get hadDeepVeinThrombosis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_I26);
  }
  set hadDeepVeinThrombosis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.deepVeinThrombosis,
      ConditionType.OLI_MEDCOND_I26,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBloodDisorder,
      hasDiagnosis
    );
  }

  //Other blood
  get otherBloodDisorder(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_HEMATOLOGYIMMUNOLOGYOTHER);
  }
  get hasOtherBloodDisorder(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_HEMATOLOGYIMMUNOLOGYOTHER);
  }
  set hasOtherBloodDisorder(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherBloodDisorder,
      ConditionType.MM_MEDCOND_HEMATOLOGYIMMUNOLOGYOTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBloodDisorder,
      hasDiagnosis
    );
  }

  //Brain tumor
  get brainTumor(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_BRAINTUMOR);
  }
  get hadBrainTumor(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_BRAINTUMOR);
  }
  set hadBrainTumor(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.brainTumor,
      ConditionType.MM_MEDCOND_BRAINTUMOR,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBrainDisorder,
      hasDiagnosis
    );
  }

  //Migrane
  get migrane(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_G43);
  }
  get hadMigrane(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_G43);
  }
  set hadMigrane(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.migrane,
      ConditionType.OLI_MEDCOND_G43,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBrainDisorder,
      hasDiagnosis
    );
  }

  // Multiple Sclerosis
  get multipleSclerosis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_G35);
  }
  get hadMultipleSclerosis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_G35);
  }
  set hadMultipleSclerosis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.multipleSclerosis,
      ConditionType.OLI_MEDCOND_G35,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBrainDisorder,
      hasDiagnosis
    );
  }

  //Traumatic Brain Injury
  get traumaticBrainInjury(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_S09);
  }
  get hadTraumaticBrainInjury(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_S09);
  }
  set hadTraumaticBrainInjury(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.traumaticBrainInjury,
      ConditionType.OLI_MEDCOND_S09,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBrainDisorder,
      hasDiagnosis
    );
  }

  // Epilepsy or seizures
  get epilepsyOrSeizures(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_G40);
  }
  get hadEpilepsyOrSeizures(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_G40);
  }
  set hadEpilepsyOrSeizures(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.epilepsyOrSeizures,
      ConditionType.OLI_MEDCOND_G40,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBrainDisorder,
      hasDiagnosis,
      TreatmentType.OLI_TREATTYPE_MED
    );
  }

  // Other Brain
  get otherBrain(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_G93);
  }
  get hasOtherBrain(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_G93);
  }
  set hasOtherBrain(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherBrain,
      ConditionType.OLI_MEDCOND_G93,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBrainDisorder,
      hasDiagnosis
    );
  }

  // Anxiety and Depression
  get anxietyDepression(): MedicalCondition {
    return (
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_ANXIETYDEPRESSION) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_ANXIETY) ||
      this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_DEPRESSION)
    );
  }
  get hasAnxietyDepression(): boolean {
    return (
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_ANXIETYDEPRESSION) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_ANXIETY) ||
      this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_DEPRESSION)
    );
  }
  set hasAnxietyDepression(hasDiagnosis: boolean) {
    const isSetOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder;
    this.setMedicalConditionAndTreatment(
      this.anxietyDepression,
      ConditionType.MM_MEDCOND_ANXIETYDEPRESSION,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_OTHER
    );
  }
  get anxietyDepressionTreatmentType(): TreatmentType {
    return (
      this.anxietyDepression &&
      this.anxietyDepression.medicalTreatment &&
      this.anxietyDepression.medicalTreatment.treatmentType
    );
  }
  set anxietyDepressionTreatmentType(treatmentType: TreatmentType) {
    if (this.anxietyDepression && this.anxietyDepression.medicalTreatment) {
      this.anxietyDepression.medicalTreatment.optionSelected = treatmentType !== TreatmentType.OLI_UNKNOWN;
      this.anxietyDepression.medicalTreatment.treatmentType = treatmentType;
    }
  }
  // Anxiety or Depression controlled for 6 months
  get isAnxietyDepressionControlled(): string {
    return this.anxietyDepression && this.anxietyDepression.conditionStatus;
  }
  set isAnxietyDepressionControlled(controlled: string) {
    if (controlled === ConditionStatus.MM_CONDSTAT_CONTROLLED) {
      this.anxietyDepression.medicalTreatment.treatmentAmt = 6;
      this.anxietyDepression.medicalTreatment.treatmentFrequencyMode = Frequency.OLI_USAGEFREQ_PERMONTH;
      this.anxietyDepression.conditionStatus = ConditionStatus.MM_CONDSTAT_CONTROLLED;
    } else {
      this.anxietyDepression.medicalTreatment.treatmentAmt = null;
      this.anxietyDepression.medicalTreatment.treatmentFrequencyMode = null;
      this.anxietyDepression.conditionStatus = ConditionStatus.OLI_CONDSTAT_ONGOING;
    }
  }
  get anxietyDepressionDosageChange(): boolean {
    if (this.anxietyDepression && this.anxietyDepression.medicalTreatment.treatmentDesc) {
      return this.anxietyDepression.medicalTreatment.treatmentDesc === TreatmentStatus.MM_TRTMNTSTAT_DOSAGECHANGE;
    }

    return undefined;
  }
  set anxietyDepressionDosageChange(dosageChanged: boolean) {
    if (this.anxietyDepression && this.anxietyDepression.medicalTreatment) {
      this.anxietyDepression.medicalTreatment.treatmentDesc = dosageChanged
        ? TreatmentStatus.MM_TRTMNTSTAT_DOSAGECHANGE
        : TreatmentStatus.OLI_UNKNOWN;
    }
  }

  //ADHD
  get ADHD(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_ADHD);
  }
  get hasADHD(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_ADHD);
  }
  set hasADHD(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.ADHD,
      ConditionType.MM_MEDCOND_ADHD,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Bipolar
  get bipolarDisorder(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_F31);
  }
  get hasBipolarDisorder(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_F31);
  }
  set hasBipolarDisorder(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.bipolarDisorder,
      ConditionType.OLI_MEDCOND_F31,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Eating disorder
  get eatingDisorder(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_F50);
  }
  get hasEatingDisorder(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_F50);
  }
  set hasEatingDisorder(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.eatingDisorder,
      ConditionType.OLI_MEDCOND_F50,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Intermittent explosive disorder
  get intExpDisorder(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_IED);
  }
  get hasIntExpDisorder(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_IED);
  }
  set hasIntExpDisorder(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.intExpDisorder,
      ConditionType.MM_MEDCOND_IED,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //OCD
  get OCD(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_F42);
  }
  get hasOCD(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_F42);
  }
  set hasOCD(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.OCD,
      ConditionType.OLI_MEDCOND_F42,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Personality Disorder
  get personalityDisorder(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_F60);
  }
  get hasPersonalityDisorder(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_F60);
  }
  set hasPersonalityDisorder(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.personalityDisorder,
      ConditionType.OLI_MEDCOND_F60,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Post traumatic stress disorder (PTSD)
  get PTSD(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_PTSD);
  }
  get hasPTSD(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_PTSD);
  }
  set hasPTSD(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.PTSD,
      ConditionType.OLI_MEDCOND_PTSD,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Psychosis
  get psychosis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_F29);
  }
  get hasPsychosis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_F29);
  }
  set hasPsychosis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.psychosis,
      ConditionType.OLI_MEDCOND_F29,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Schizophrenia
  get schizophrenia(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_F20);
  }
  get hasSchizophrenia(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_F20);
  }
  set hasSchizophrenia(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.schizophrenia,
      ConditionType.OLI_MEDCOND_F20,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Suicidal thoughts
  get suicidalThoughts(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_SUICIDE);
  }
  get hasSuicidalThoughts(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_SUICIDE);
  }
  set hasSuicidalThoughts(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.suicidalThoughts,
      ConditionType.OLI_MEDCOND_SUICIDE,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  //Other Emotional
  get otherEmotional(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_PSYCHIATRICOTHER);
  }
  get hasOtherEmotional(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_PSYCHIATRICOTHER);
  }
  set hasOtherEmotional(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherEmotional,
      ConditionType.MM_MEDCOND_PSYCHIATRICOTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEmotionalDisorder,
      hasDiagnosis
    );
  }

  // Colon Plyps
  get colonPolyps(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_COLONPOLYPS);
  }
  get hasColonPolyps(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_COLONPOLYPS);
  }
  set hasColonPolyps(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.colonPolyps,
      ConditionType.MM_MEDCOND_COLONPOLYPS,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder,
      hasDiagnosis,
      undefined,
      TestType.OLI_TESTTYPE_COLON
    );
  }

  // Chrons Disease
  get chronsDisease(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_K50);
  }
  get hasChronsDisease(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_K50);
  }
  set hasChronsDisease(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.chronsDisease,
      ConditionType.OLI_MEDCOND_K50,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder,
      hasDiagnosis
    );
  }

  // Colitis Ulcerative
  get colitisUlcerative(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_K51);
  }
  get hasColitisUlcerative(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_K51);
  }
  set hasColitisUlcerative(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.colitisUlcerative,
      ConditionType.OLI_MEDCOND_K51,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder,
      hasDiagnosis
    );
  }

  // Diverticulitis
  get diverticulitis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_DIVERTICULITIS);
  }
  get hasDiverticulitis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_DIVERTICULITIS);
  }
  set hasDiverticulitis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.diverticulitis,
      ConditionType.MM_MEDCOND_DIVERTICULITIS,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder,
      hasDiagnosis
    );
  }

  // HepatitusBCD
  get hepatitusBCD(): MedicalCondition {
    return (
      this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_HEPATITIS) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_HEPATITISB) ||
      this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_HEPC) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_HEPATITISD)
    );
  }
  get hasHepatitusBCD(): boolean {
    return (
      this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_HEPATITIS) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_HEPATITISB) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_HEPATITISD) ||
      this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_HEPC)
    );
  }
  set hasHepatitusBCD(hasDiagnosis: boolean) {
    const setOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder;
    if (hasDiagnosis && setOnParent) {
      this.setMedicalCondition(
        this.hepatitusBCD,
        ConditionType.OLI_MEDCOND_HEPATITIS,
        undefined,
        setOnParent,
        hasDiagnosis
      );
    } else {
      this.medicalConditions.forEach((medicalConditionObj, index) => {
        if (
          medicalConditionObj.conditionType === ConditionType.OLI_MEDCOND_HEPATITIS ||
          medicalConditionObj.conditionType === ConditionType.MM_MEDCOND_HEPATITISB ||
          medicalConditionObj.conditionType === ConditionType.MM_MEDCOND_HEPATITISD ||
          medicalConditionObj.conditionType === ConditionType.OLI_MEDCOND_HEPC
        ) {
          this.medicalConditions[index].medicalTreatments = [];
          this.medicalConditions[index].medicalPreventions = [];
          this.medicalConditions[index].conditionType = null;
          this.medicalConditions[index].subConditionType = null;
        }
      });
    }
  }

  // Pancreatitis
  get pancreatitis(): MedicalCondition {
    return (
      this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_PANCRE) ||
      this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_K85)
    );
  }
  get hasPancreatitis(): boolean {
    return (
      this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_PANCRE) ||
      this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_K85)
    );
  }
  set hasPancreatitis(hasDiagnosis: boolean) {
    const setOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder;
    if (hasDiagnosis && setOnParent) {
      this.setMedicalCondition(
        this.pancreatitis,
        ConditionType.OLI_MEDCOND_PANCRE,
        undefined,
        setOnParent,
        hasDiagnosis
      );
    } else {
      this.medicalConditions.forEach((medicalConditionObj, index) => {
        if (
          medicalConditionObj.conditionType === ConditionType.OLI_MEDCOND_PANCRE ||
          medicalConditionObj.conditionType === ConditionType.OLI_MEDCOND_K85
        ) {
          this.medicalConditions[index].medicalTreatments = [];
          this.medicalConditions[index].medicalPreventions = [];
          this.medicalConditions[index].conditionType = null;
          this.medicalConditions[index].subConditionType = null;
        }
      });
    }
  }

  // Ulcer
  get ulcer(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_ULCER);
  }
  get hasUlcer(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_ULCER);
  }
  set hasUlcer(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.ulcer,
      ConditionType.OLI_MEDCOND_ULCER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder,
      hasDiagnosis
    );
  }

  //miscellaneous condition
  get miscellaneousCondition(): MedicalCondition {
    return this.getMedicalConditionByType(MedicalConditionType.MISCELLANEOUS_CONDITION);
  }

  // Weight Loss Treatment
  get weightLossCondition(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_DIGESTIVEUNKNOWN);
  }
  get weightLossSurgery(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      this.weightLossCondition,
      ConditionType.MM_MEDCOND_DIGESTIVEUNKNOWN,
      TreatmentType.MM_TREATTYPE_SURGERYWEIGHTLOSS
    );
  }
  get hasWeightLossSurgery(): boolean {
    return this.medicalTreatmentExistsByType(
      this.weightLossCondition,
      ConditionType.MM_MEDCOND_DIGESTIVEUNKNOWN,
      TreatmentType.MM_TREATTYPE_SURGERYWEIGHTLOSS
    );
  }
  set hasWeightLossSurgery(hasDiagnosis: boolean) {
    const isSetOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder;
    this.setMedicalConditionAndTreatment(
      this.weightLossCondition,
      ConditionType.MM_MEDCOND_DIGESTIVEUNKNOWN,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_SURGERYWEIGHTLOSS
    );
  }

  // other Digestive
  get otherDigestive(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_DIGESTIVEOTHER);
  }
  get hasOtherDigestive(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_DIGESTIVEOTHER);
  }
  set hasOtherDigestive(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherDigestive,
      ConditionType.MM_MEDCOND_DIGESTIVEOTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadDigestiveDisorder,
      hasDiagnosis
    );
  }

  // Ankylosing Spondylitis
  get ankylosingSpondylitis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_M45);
  }
  get hasAnkylosingSpondylitis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_M45);
  }
  set hasAnkylosingSpondylitis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.ankylosingSpondylitis,
      ConditionType.OLI_MEDCOND_M45,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBoneMuscularDisorder,
      hasDiagnosis
    );
  }

  // Arthritis
  get arthritis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_ARTHRI);
  }
  get hasArthritis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_ARTHRI);
  }
  set hasArthritis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.arthritis,
      ConditionType.OLI_MEDCOND_ARTHRI,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadBoneMuscularDisorder,
      hasDiagnosis
    );
  }

  // Loss of hearing vision and speech
  get acousticNeuroma(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_ACOUSTICNEUROMA);
  }
  get hasAcousticNeuroma(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_ACOUSTICNEUROMA);
  }
  set hasAcousticNeuroma(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.arthritis,
      ConditionType.MM_MEDCOND_ACOUSTICNEUROMA,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEENT,
      hasDiagnosis
    );
  }

  // Loss of hearing vision and speech
  get lossOfHVS(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_HVSLOSS);
  }
  get hasLossOfHVS(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_HVSLOSS);
  }
  set hasLossOfHVS(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.lossOfHVS,
      ConditionType.MM_MEDCOND_HVSLOSS,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEENT,
      hasDiagnosis
    );
  }

  // Other EENT
  get otherEENT(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_EENTOTHER);
  }
  get hasOtherEENT(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_EENTOTHER);
  }
  set hasOtherEENT(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherEENT,
      ConditionType.MM_MEDCOND_EENTOTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadEENT,
      hasDiagnosis
    );
  }

  // sarcoidosis
  get sarcoidosis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_D86);
  }
  get hasSarcoidosis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_D86);
  }
  set hasSarcoidosis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.sarcoidosis,
      ConditionType.OLI_MEDCOND_D86,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRespiratoryDisorder,
      hasDiagnosis
    );
  }

  // Asthma
  get asthma(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_J45);
  }
  get hasAsthma(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_J45);
  }
  set hasAsthma(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.asthma,
      ConditionType.OLI_MEDCOND_J45,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRespiratoryDisorder,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_STEROIDS
    );
  }

  // pulmonary nodules
  get pulmonaryNodules(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_PULMNODULE);
  }
  get hasPulmonaryNodules(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_PULMNODULE);
  }
  set hasPulmonaryNodules(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.pulmonaryNodules,
      ConditionType.OLI_MEDCOND_PULMNODULE,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRespiratoryDisorder,
      hasDiagnosis
    );
  }

  // sleep apnea
  get sleepApnea(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_SLEEP);
  }
  get hasSleepApnea(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_SLEEP);
  }
  set hasSleepApnea(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.sleepApnea,
      ConditionType.OLI_MEDCOND_SLEEP,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRespiratoryDisorder,
      hasDiagnosis
    );
  }

  // Covid
  get covid(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_COVID);
  }
  get hadCovid(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_COVID);
  }
  set hadCovid(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.covid,
      ConditionType.MM_MEDCOND_COVID,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRespiratoryDisorder,
      hasDiagnosis
    );
  }

  // other respiratory
  get otherRespiratory(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_J98);
  }
  get hasOtherRespiratory(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_J98);
  }
  set hasOtherRespiratory(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherRespiratory,
      ConditionType.OLI_MEDCOND_J98,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRespiratoryDisorder,
      hasDiagnosis
    );
  }

  // Lyme Disease
  get lymeDisease(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_LYMEDISEASE);
  }
  get hasLymeDisease(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_LYMEDISEASE);
  }
  set hasLymeDisease(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.lymeDisease,
      ConditionType.MM_MEDCOND_LYMEDISEASE,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRhumatologicDisorder,
      hasDiagnosis
    );
  }

  // Epstein Barr
  get epsteinBarr(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_EPSTEINBARR);
  }
  get hadEpsteinBarr(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_EPSTEINBARR);
  }
  set hadEpsteinBarr(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.epsteinBarr,
      ConditionType.MM_MEDCOND_EPSTEINBARR,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRhumatologicDisorder,
      hasDiagnosis
    );
  }

  // Other Rheumatology
  get otherRheumatoid(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_RHEUMATICOTHER);
  }
  get hasOtherRheumatoid(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_RHEUMATICOTHER);
  }
  set hasOtherRheumatoid(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherRheumatoid,
      ConditionType.MM_MEDCOND_RHEUMATICOTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadRhumatologicDisorder,
      hasDiagnosis
    );
  }

  // Diabetes
  get diabetes(): MedicalCondition {
    return (
      this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_DIABETES) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_DIABETES1) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_DIABETES2) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_DIABETESOTHER) ||
      this.getMedicalConditionByType(ConditionType.MM_MEDCOND_PREDIABETES)
    );
  }
  get hasDiabetes(): boolean {
    return (
      this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_DIABETES) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_DIABETES1) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_DIABETES2) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_DIABETESOTHER) ||
      this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_PREDIABETES)
    );
  }
  set hasDiabetes(hasDiagnosis: boolean) {
    const setOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hasDiabetesThyroid;
    if (hasDiagnosis && setOnParent) {
      this.setMedicalCondition(this.diabetes, ConditionType.OLI_MEDCOND_DIABETES, undefined, setOnParent, hasDiagnosis);
    } else {
      this.medicalConditions.forEach((medicalConditionObj, index) => {
        if (
          medicalConditionObj.conditionType === ConditionType.OLI_MEDCOND_DIABETES ||
          medicalConditionObj.conditionType === ConditionType.MM_MEDCOND_PREDIABETES ||
          medicalConditionObj.conditionType === ConditionType.MM_MEDCOND_DIABETES1 ||
          medicalConditionObj.conditionType === ConditionType.MM_MEDCOND_DIABETES2 ||
          medicalConditionObj.conditionType === ConditionType.MM_MEDCOND_DIABETESOTHER
        ) {
          this.medicalConditions[index].medicalTreatments = [];
          this.medicalConditions[index].medicalPreventions = [];
          this.medicalConditions[index].conditionType = null;
          this.medicalConditions[index].subConditionType = null;
        }
      });
    }
  }

  // Gestational Diabetes
  get gestationalDiabetes(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_GESTATIONALDIABETES);
  }
  get hasGestationalDiabetes(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_GESTATIONALDIABETES);
  }
  set hasGestationalDiabetes(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.gestationalDiabetes,
      ConditionType.MM_MEDCOND_GESTATIONALDIABETES,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hasDiabetesThyroid,
      hasDiagnosis
    );
  }

  // Prolactinoma
  get prolactinoma(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_PROLACTINOMA);
  }
  get hasProlactinoma(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_PROLACTINOMA);
  }
  set hasProlactinoma(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.prolactinoma,
      ConditionType.MM_MEDCOND_PROLACTINOMA,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hasDiabetesThyroid,
      hasDiagnosis
    );
  }

  // Thyroid Cysts Surgery
  get thyroidCysts(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_THYROIDCYSTS);
  }
  get hadThyroidCystsSurgery(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_THYROIDCYSTS);
  }
  set hadThyroidCystsSurgery(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.thyroidCysts,
      ConditionType.MM_MEDCOND_THYROIDCYSTS,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hasDiabetesThyroid,
      hasDiagnosis
    );
  }

  // Other Endocrine
  get otherEndocrine(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_E34);
  }
  get hasOtherEndocrine(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_E34);
  }
  set hasOtherEndocrine(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherEndocrine,
      ConditionType.OLI_MEDCOND_E34,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hasDiabetesThyroid,
      hasDiagnosis
    );
  }

  // Elevated PSA
  get elevatedPSA(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_ELEVATEDPSA);
  }
  get hasElevatedPSA(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_ELEVATEDPSA);
  }
  set hasElevatedPSA(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.elevatedPSA,
      ConditionType.MM_MEDCOND_ELEVATEDPSA,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadUrninaryTrackDisorder,
      hasDiagnosis
    );
  }

  // Kidney stones
  get kidneyStones(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_KIDNEYSTONE);
  }
  get hadKidneyStones(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_KIDNEYSTONE);
  }
  set hadKidneyStones(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.kidneyStones,
      ConditionType.OLI_MEDCOND_KIDNEYSTONE,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadUrninaryTrackDisorder,
      hasDiagnosis
    );
  }

  // Polycystic Kidney Disease
  get polycysticKidneyDisease(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_POLYCYSTICKIDNEY);
  }
  get hasPolycysticKidneyDisease(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_POLYCYSTICKIDNEY);
  }
  set hasPolycysticKidneyDisease(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.polycysticKidneyDisease,
      ConditionType.MM_MEDCOND_POLYCYSTICKIDNEY,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadUrninaryTrackDisorder,
      hasDiagnosis
    );
  }

  // hasProstatitis
  get prostatitis(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_PROSTATITIS);
  }
  get hasProstatitis(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_PROSTATITIS);
  }
  set hasProstatitis(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.prostatitis,
      ConditionType.MM_MEDCOND_PROSTATITIS,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadUrninaryTrackDisorder,
      hasDiagnosis
    );
  }

  // Kidney Infection
  get kidneyInfection(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_KIDNEYINFECTION);
  }
  get hasKidneyInfection(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_KIDNEYINFECTION);
  }
  set hasKidneyInfection(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.kidneyInfection,
      ConditionType.MM_MEDCOND_KIDNEYINFECTION,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadUrninaryTrackDisorder,
      hasDiagnosis
    );
  }

  // Other kidney
  get otherKidney(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_N28);
  }
  get hasOtherKidney(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_N28);
  }
  set hasOtherKidney(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherKidney,
      ConditionType.OLI_MEDCOND_N28,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadUrninaryTrackDisorder,
      hasDiagnosis
    );
  }

  // Abnormal Pap smear
  get abnormalPapSmear(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get abnormalPapSmearTest(): MedicalPrevention {
    return this.getMedicalTestByType(
      this.abnormalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.OLI_TESTTYPE_PAP,
      undefined,
      QualitativeResult.OLI_QUALVALUE_ABNORMAL
    );
  }
  get hadAbnormalPapSmear(): boolean {
    return this.medicalTestExistsByType(
      this.abnormalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.OLI_TESTTYPE_PAP,
      undefined,
      QualitativeResult.OLI_QUALVALUE_ABNORMAL
    );
  }
  set hadAbnormalPapSmear(hasDiagnosis: boolean) {
    const isSetOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive;
    this.setMedicalConditionAndTreatment(
      this.abnormalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      undefined,
      TestType.OLI_TESTTYPE_PAP,
      undefined,
      QualitativeResult.OLI_QUALVALUE_ABNORMAL
    );
  }

  // Normal Pap smear
  get normalPapSmear(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get normalPapSmearTest(): MedicalPrevention {
    return this.getMedicalTestByType(
      this.normalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.OLI_TESTTYPE_PAP,
      undefined,
      QualitativeResult.OLI_QUALVALUE_NORMAL
    );
  }
  get hasNormalPapSmear(): boolean {
    return this.medicalTestExistsByType(
      this.normalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.OLI_TESTTYPE_PAP,
      undefined,
      QualitativeResult.OLI_QUALVALUE_NORMAL
    );
  }
  set hasNormalPapSmear(hasDiagnosis: boolean) {
    const isSetOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive;
    this.setMedicalConditionAndTreatment(
      this.normalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      isSetOnParent,
      hasDiagnosis && this.hadAbnormalPapSmear,
      undefined,
      TestType.OLI_TESTTYPE_PAP,
      undefined,
      QualitativeResult.OLI_QUALVALUE_NORMAL
    );
  }

  // Cervical Biopsy
  get cervicalBiopsy(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get cervicalBiopsyTest(): MedicalPrevention {
    return this.getMedicalTestByType(
      this.cervicalBiopsy,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.MM_TESTTYPE_BIOPSYCERVICAL
    );
  }
  get hadCervicalBiopsy(): boolean {
    return this.medicalTestExistsByType(
      this.normalPapSmear,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.MM_TESTTYPE_BIOPSYCERVICAL
    );
  }
  set hadCervicalBiopsy(hasDiagnosis: boolean) {
    if (typeof hasDiagnosis !== undefined && typeof hasDiagnosis !== null) {
      const isSetOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive;
      this.setMedicalConditionAndTreatment(
        this.cervicalBiopsy,
        MedicalConditionType.MISCELLANEOUS_CONDITION,
        undefined,
        isSetOnParent,
        hasDiagnosis,
        undefined,
        TestType.MM_TESTTYPE_BIOPSYCERVICAL
      );
    }
  }

  // HPV
  get hpv(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_MEDCOND_HPV);
  }
  get hasHPV(): boolean {
    return this.medicalConditionExistsByType(ConditionType.OLI_MEDCOND_HPV);
  }
  set hasHPV(hasDiagnosis: boolean) {
    const isSetOnParent = this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive;
    this.setMedicalCondition(
      this.hpv,
      ConditionType.OLI_MEDCOND_HPV,
      undefined,
      isSetOnParent,
      hasDiagnosis && this.hadAbnormalPapSmear
    );
    // linking abnormal pap smear with hpv
    if (this.hpv && this.abnormalPapSmearTest) {
      this.hpv.medicalPreventions.push(this.abnormalPapSmearTest);
    }
  }

  // Breast or Ovarian cysts
  get breastOvarianCysts(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_BREASTOVARIANCYST);
  }
  get hasBreastOvarianCysts(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_BREASTOVARIANCYST);
  }
  set hasBreastOvarianCysts(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.breastOvarianCysts,
      ConditionType.MM_MEDCOND_BREASTOVARIANCYST,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive,
      hasDiagnosis
    );
  }

  // Other Uterus
  get otherUterus(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.MM_MEDCOND_UCOBOTHER);
  }
  get hasOtherUterus(): boolean {
    return this.medicalConditionExistsByType(ConditionType.MM_MEDCOND_UCOBOTHER);
  }
  set hasOtherUterus(hasDiagnosis: boolean) {
    this.setMedicalCondition(
      this.otherUterus,
      ConditionType.MM_MEDCOND_UCOBOTHER,
      undefined,
      this.customer.application.primaryPolicy.insured.personalHistory.hadIDReproductive,
      hasDiagnosis
    );
  }

  // Blood Transfusion treatment
  get bloodTransfusionCondition(): MedicalCondition {
    return this.getMedicalConditionByType(MedicalConditionType.BLOOD_TRANSFUSION_CONDITION);
  }
  get hadBloodTransfusion(): boolean {
    return this.medicalTreatmentExistsByType(
      this.bloodTransfusionCondition,
      MedicalConditionType.BLOOD_TRANSFUSION_CONDITION,
      TreatmentType.MM_TREATTYPE_BLOODTRANSFUSION
    );
  }
  set hadBloodTransfusion(hasDiagnosis: boolean) {
    if (hasDiagnosis && this.bloodTransfusionCondition && this.bloodTransfusionCondition.medicalTreatments.length) {
      return;
    }

    if (!hasDiagnosis && !this.bloodTransfusionCondition) {
      return;
    }

    const isSetOnParent = hasDiagnosis;
    this.setMedicalConditionAndTreatment(
      this.bloodTransfusionCondition,
      MedicalConditionType.BLOOD_TRANSFUSION_CONDITION,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_BLOODTRANSFUSION,
      undefined,
      undefined,
      undefined,
      hasDiagnosis
    );
  }
  get bloodTransfusionTreatments(): MedicalTreatment[] {
    return this.getAllMedicalTreatmentsByType(
      this.bloodTransfusionCondition,
      MedicalConditionType.BLOOD_TRANSFUSION_CONDITION,
      TreatmentType.MM_TREATTYPE_BLOODTRANSFUSION
    );
  }
  bloodTransfusionTreatmentsAdd() {
    this.setMedicalConditionAndTreatment(
      this.bloodTransfusionCondition,
      MedicalConditionType.BLOOD_TRANSFUSION_CONDITION,
      undefined,
      true,
      true,
      TreatmentType.MM_TREATTYPE_BLOODTRANSFUSION,
      undefined,
      undefined,
      undefined,
      true,
      true
    );
  }
  bloodTransfusionTreatmentsRemove(index: number) {
    this.bloodTransfusionCondition.medicalTreatments.splice(index, 1);
  }

  // Heart Surgery treatment
  get heartSurgeryCondition(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get heartSurgery(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      this.heartSurgeryCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.MM_TREATTYPE_HEARTSURGERY
    );
  }
  get hadHeartSurgery(): boolean {
    return this.medicalTreatmentExistsByType(
      this.heartSurgeryCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.MM_TREATTYPE_HEARTSURGERY
    );
  }
  set hadHeartSurgery(hasDiagnosis: boolean) {
    const isSetOnParent = hasDiagnosis;
    this.setMedicalConditionAndTreatment(
      this.heartSurgeryCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_HEARTSURGERY
    );
  }

  // prostatectomy treatment
  get prostatectomyCondition(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get prostatectomy(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      this.prostatectomyCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.MM_TREATTYPE_PROSTATESURGERY
    );
  }
  get hadProstatectomy(): boolean {
    return this.medicalTreatmentExistsByType(
      this.prostatectomyCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.MM_TREATTYPE_PROSTATESURGERY
    );
  }
  set hadProstatectomy(hasDiagnosis: boolean) {
    const isSetOnParent = hasDiagnosis;
    this.setMedicalConditionAndTreatment(
      this.prostatectomyCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_PROSTATESURGERY
    );
  }

  // mastectomy treatment
  get mastectomyCondition(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get mastectomy(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      this.mastectomyCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.MM_TREATTYPE_MASTECTOMY
    );
  }
  get hadMastectomy(): boolean {
    return this.medicalTreatmentExistsByType(
      this.mastectomyCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.MM_TREATTYPE_MASTECTOMY
    );
  }
  set hadMastectomy(hasDiagnosis: boolean) {
    const isSetOnParent = hasDiagnosis;
    this.setMedicalConditionAndTreatment(
      this.mastectomyCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      isSetOnParent,
      hasDiagnosis,
      TreatmentType.MM_TREATTYPE_MASTECTOMY
    );
  }

  // Overnight Hospitalized treatment
  get overnightHospitalizationCondition(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get overnightHospitalization(): MedicalTreatment {
    return this.getMedicalTreatmentByType(
      this.overnightHospitalizationCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.OLI_TREATTYPE_HOSP
    );
  }
  get hadOvernightHospitalization(): boolean {
    return this.medicalTreatmentExistsByType(
      this.overnightHospitalizationCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TreatmentType.OLI_TREATTYPE_HOSP
    );
  }
  set hadOvernightHospitalization(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.overnightHospitalizationCondition,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      true,
      hasDiagnosis,
      TreatmentType.OLI_TREATTYPE_HOSP
    );
    if (this.overnightHospitalization) {
      this.overnightHospitalization.timePeriod = TimePeriod.PAST_FIVE_YEARS;
    }
  }
  get someUnknownCondition(): MedicalCondition {
    return this.getMedicalConditionByType(ConditionType.OLI_UNKNOWN);
  }

  // Physical Exam
  get physicalExam(): MedicalCondition {
    return this.miscellaneousCondition;
  }
  get physicalExamTest(): MedicalPrevention {
    return this.getMedicalTestByType(
      this.physicalExam,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.OLI_TESTTYPE_EXAM
    );
  }
  get hadPhysicalExam(): boolean {
    return this.medicalTestExistsByType(
      this.physicalExam,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      TestType.OLI_TESTTYPE_EXAM
    );
  }
  set hadPhysicalExam(hasDiagnosis: boolean) {
    this.setMedicalConditionAndTreatment(
      this.physicalExam,
      MedicalConditionType.MISCELLANEOUS_CONDITION,
      undefined,
      true,
      hasDiagnosis,
      undefined,
      TestType.OLI_TESTTYPE_EXAM
    );
    if (this.physicalExamTest) {
      this.physicalExamTest.timePeriod = TimePeriod.PAST_THREE_YEARS;
    }
  }

  //alcohol
  get alcohol(): MedicalCondition {
    return this.getMedicalConditionByType(SubstanceType.OLI_SUBSTANCE_ALCOHOL);
  }
  // Drugs
  get drugs(): MedicalCondition {
    return this.getMedicalConditionByType(SubstanceType.MM_SUBSTANCE_DRUGS);
  }

  @property()
  usedHabitFormingDrugs: boolean;

  get hadHabitFormingDrugs(): boolean {
    return this.usedHabitFormingDrugs;
  }
  set hadHabitFormingDrugs(habitDrugs: boolean) {
    this.usedHabitFormingDrugs = habitDrugs;
    this.setMedicalConditionAndTreatment(
      this.drugs,
      SubstanceType.MM_SUBSTANCE_DRUGS,
      SubstanceType.MM_SUBSTANCE_DRUGS,
      habitDrugs,
      habitDrugs
    );
  }

  // alcohol
  get haveBeenCounselledAlcohol(): boolean {
    return (
      this.alcohol &&
      !!this.alcohol.medicalTreatments.find(
        (medicalTreatment) =>
          medicalTreatment && medicalTreatment.treatmentType === TreatmentType.MM_TREATTYPE_COUNSELING
      )
    );
  }
  //set for alcohol
  set haveBeenCounselledAlcohol(usage: boolean) {
    this.setMedicalConditionAndTreatment(
      this.alcohol,
      SubstanceType.OLI_SUBSTANCE_ALCOHOL,
      undefined,
      usage,
      usage,
      TreatmentType.MM_TREATTYPE_COUNSELING
    );
  }
  // drugs
  get haveBeenCounselledDrugs(): boolean {
    return (
      this.drugs &&
      !!this.drugs.medicalTreatments.find(
        (medicalTreatment) =>
          medicalTreatment && medicalTreatment.treatmentType === TreatmentType.MM_TREATTYPE_COUNSELING
      )
    );
  }
  set haveBeenCounselledDrugs(usage: boolean) {
    this.setMedicalConditionAndTreatment(
      this.drugs,
      SubstanceType.MM_SUBSTANCE_DRUGS,
      undefined,
      usage,
      usage,
      TreatmentType.MM_TREATTYPE_COUNSELING
    );
  }

  @property()
  hadNoTreatmentOrCounsel: boolean;

  set currentUsageAlcoholDrugs(currentUsage: boolean) {
    if (this.drugs) {
      this.drugs.episodeInd = currentUsage;
    }
    if (this.alcohol) {
      this.alcohol.episodeInd = currentUsage;
    }
  }
  get currentUsageAlcoholDrugs(): boolean {
    let currentDrugUseResponse: boolean;
    let currentAlcoholUseResponse: boolean;
    let currentDrugUse: boolean = false;
    let currentAlcoholUse: boolean = false;
    if (this.drugs) {
      currentDrugUseResponse = typeof this.drugs.episodeInd !== 'undefined';
      currentDrugUse = !!this.drugs.episodeInd;
    }
    if (this.alcohol) {
      currentAlcoholUseResponse = typeof this.alcohol.episodeInd !== 'undefined';
      currentAlcoholUse = !!this.alcohol.episodeInd;
    }
    if (!currentDrugUseResponse && !currentAlcoholUseResponse) {
      return undefined;
    }
    return currentDrugUse || currentAlcoholUse;
  }
  set lastUsageAlcoholDrugs(lastUsageDate: Date) {
    if (lastUsageDate) {
      if (this.drugs) {
        this.drugs.lastEpisodeDate = lastUsageDate;
      }
      if (this.alcohol) {
        this.alcohol.lastEpisodeDate = lastUsageDate;
      }
    }
  }
  get lastUsageAlcoholDrugs(): Date {
    return (this.drugs && this.drugs.lastEpisodeDate) || (this.alcohol && this.alcohol.lastEpisodeDate);
  }
  set treatmentTypeAlcoholDrugs(treatment: string) {
    if (treatment) {
      if (this.drugs && this.drugs.medicalTreatment) {
        this.drugs.medicalTreatment.treatmentDesc = treatment;
      }
      if (this.alcohol && this.alcohol.medicalTreatment) {
        this.alcohol.medicalTreatment.treatmentDesc = treatment;
      }
    }
  }
  get treatmentTypeAlcoholDrugs(): string {
    return (
      (this.drugs && this.drugs.medicalTreatment && this.drugs.medicalTreatment.treatmentDesc) ||
      (this.alcohol && this.alcohol.medicalTreatment && this.alcohol.medicalTreatment.treatmentDesc)
    );
  }
  set treatmentDetailsAlcoholDrugs(details: DiagnosisShort) {
    if (details) {
      if (this.drugs && this.drugs.medicalTreatment) {
        this.drugs.medicalTreatment.details = details;
      }
      if (this.alcohol && this.alcohol.medicalTreatment) {
        this.alcohol.medicalTreatment.details = details;
      }
    }
  }
  get treatmentDetailsAlcoholDrugs(): DiagnosisShort {
    return (
      (this.drugs && this.drugs.medicalTreatment && this.drugs.medicalTreatment.details) ||
      (this.alcohol && this.alcohol.medicalTreatment && this.alcohol.medicalTreatment.details)
    );
  }
  set lastTreatmentAlcoholDrugs(lastTreatment: Date) {
    if (lastTreatment) {
      if (this.drugs && this.drugs.medicalTreatment) {
        this.drugs.medicalTreatment.dateLastSeen = lastTreatment;
      }
      if (this.alcohol && this.alcohol.medicalTreatment) {
        this.alcohol.medicalTreatment.dateLastSeen = lastTreatment;
      }
    }
  }
  get lastTreatmentAlcoholDrugs(): Date {
    return (
      (this.drugs && this.drugs.medicalTreatment && this.drugs.medicalTreatment.dateLastSeen) ||
      (this.alcohol && this.alcohol.medicalTreatment.dateLastSeen)
    );
  }
  set relapseIndAlcoholDrugs(relapse: boolean) {
    if (this.drugs) {
      this.drugs.symptomsInd = relapse;
    }
    if (this.alcohol) {
      this.alcohol.symptomsInd = relapse;
    }
  }
  get relapseIndAlcoholDrugs(): boolean {
    let drugRelapseResponse: boolean;
    let alcoholRelapseResponse: boolean;
    let drugRelapse: boolean = false;
    let alcoholRelapse: boolean = false;
    if (this.drugs) {
      drugRelapseResponse = typeof this.drugs.symptomsInd !== 'undefined';
      drugRelapse = !!this.drugs.episodeInd;
    }
    if (this.alcohol) {
      alcoholRelapseResponse = typeof this.alcohol.symptomsInd !== 'undefined';
      alcoholRelapse = !!this.alcohol.symptomsInd;
    }
    if (!drugRelapseResponse && !alcoholRelapseResponse) {
      return undefined;
    }

    return drugRelapse || alcoholRelapse;
  }
  set relapseRecentAlcoholDrugs(lastRelapse: Date) {
    if (lastRelapse) {
      if (this.drugs) {
        this.drugs.firstDiagnosisDate = lastRelapse;
      }
      if (this.alcohol) {
        this.alcohol.firstDiagnosisDate = lastRelapse;
      }
    }
  }
  get relapseRecentAlcoholDrugs(): Date {
    return (this.drugs && this.drugs.firstDiagnosisDate) || (this.alcohol && this.alcohol.firstDiagnosisDate);
  }

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 5 years, have you been convicted of operating a motor vehicle while under the influence of alcohol or drugs',
  })
  mvDUI: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      "Has your driver's license been suspended or revoked, or have you been charged with operating a motor vehicle under the influence (DUI/DWI)? (In the past 5 years)",
  })
  mvSuspendedOrDUI: boolean;

  @property({
    rule: ['required'],
    values: ['0-1years', '1-2years', '2-3years', '3-5years'],
    descriptions: {
      '0-1years': '0-1 years',
      '1-2years': '1-2 years',
      '2-3years': '2-3 years',
      '3-5years': '3-5 years',
    },
    comment: 'How many years has it been since your last DUI',
  })
  mvLastDUI: string;

  @property({
    rule: ['required'],
    values: ['1', '2', '3ormore'],
    descriptions: { '1': '1', '2': '2', '3ormore': '3 or more' },
    comment: 'In the past 5 years, how many DUI convictions have you received',
  })
  mvDUIConvitions: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'In the past 3 years, have you been in a motor vehicle accident in which you were found to be at fault, convicted of a moving violation or received a driver’s license restriction or revocation',
  })
  mvAccident: boolean;

  @property({
    rule: ['required', 'numericPositive'],
    validate: 'isWithin(0,50)',
    comment: 'Moving violations in the last 5 years',
  })
  mvViolations: number;

  @property({ comment: 'At-Fault Accident' })
  mvAccidentAtFault: boolean;

  @property({ type: Array, getType: () => AccidentAtFault })
  mvAccidentAtFaults: Array<AccidentAtFault> = [];

  @property({ comment: 'Whether speeding was one of the violations' })
  mvSpeeding: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
    comment: 'Was your ECG result normal',
  })
  hasCurrentHBPressure: string;

  @property({
    rule: ['required'],
    values: ['Yes', 'No', 'NotSure'],
    descriptions: { Yes: 'Yes', No: 'No', NotSure: 'Not Sure' },
  })
  hasCurrentElevatedCholesterol: string;

  @property({ comment: 'Indicates high-risk pregnancy' })
  isPregnantHighRisk: boolean;

  @property()
  pregnancyHighRiskDetails: DiagnosisShort;

  @property({
    rule: ['required', 'numericPositive'],
    validate: 'isWithin(1,50)',
    comment: 'Number of speeding tickets',
  })
  mySpeedingTicketCount: number;

  @property({ type: Array, getType: () => SpeedingTicket })
  mySpeedingTickets: Array<SpeedingTicket> = [];

  @property({ comment: 'Careless/Reckless Driving' })
  mbReckless: boolean;

  @property({ type: Array, getType: () => RecklessTicket })
  mbRecklessTickets: Array<RecklessTicket> = [];

  @property({ comment: 'Indicates existence of moving violations other than the predefined' })
  mvOther: boolean;

  @property({ type: Array, getType: () => OtherMovingViolation })
  mvOthers: Array<OtherMovingViolation> = [];

  @property({ comment: 'Indicates no moving violations' })
  mvNone: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'In the past 5 years, have you been convicted of driving with a suspended or revoked license',
  })
  mvDrivingWhileSuspended: boolean;

  @property({ type: Array, getType: () => DrivingWhileSuspendedCon })
  mvDrivingWhileSuspendedCons: Array<DrivingWhileSuspendedCon> = [];

  @property({ type: Array, getType: () => DUIConviction })
  mvDUIConvictions: Array<DUIConviction> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Is your license currently suspended',
  })
  mvLicenseSuspended: boolean;

  @property({ rule: ['required'], comment: 'Details for currently suspended license' })
  mvLicenseSuspendedDetails: string;

  @property({ rule: ['required'], comment: 'Details for currently suspended license' })
  mvLicenseSuspendedPast: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Disabled For more than 30 days',
  })
  recentlyDisabled: boolean;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
    comment: 'Date disability began',
  })
  disabledStartDate: Date;

  @property({
    rule: ['required', 'date', 'pastDate', 'shortdate'],
    comment: 'Date recovered from disability',
  })
  disabledRecoveryDate: Date;

  @property({
    rule: ['required'],
    comment: 'Cause for disability',
  })
  disabledCause: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Are you currently disabled (unable to work, attend school or perform normal activities) or applying for any disability benefits',
  })
  disabled: boolean;

  @property({ comment: 'Details for listed disability' })
  disabledDetails: string;

  @property({ comment: 'Diagnosis details for the listed disability' })
  disabilityDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['Yes', 'No'],
    descriptions: { Yes: 'Yes', No: 'No/Not Sure' },
    comment: 'Do you anticipate any foreign travel within the next 2 years',
  })
  anticipatingForeignTravel: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Will your travel be limited to Western Europe, Australia, Japan, South Korea, Canada, New Zealand or Israel (excluding Gaza and the West Bank)',
  })
  ftBenign: boolean;

  @property({
    rule: ['required', 'numericPositive'],
    validate: 'isWithin(1,1000)',
    comment: 'Estimated duration of stay (in days)',
  })
  ftDuration: number;

  @property({
    rule: ['required'],
    values: ['Pleasure', 'Business/Work', 'Education', 'Other'],
    descriptions: function () {
      const value = {
        'Business/Work': 'Business/Work',
        'Education': 'Education',
        'Other': 'Other',
      };

      if (this.customer && this.customer.application && this.customer.application.primaryPolicy.isICCApplication()) {
        value['Pleasure'] = 'Pleasure/Family';
      } else {
        value['Pleasure'] = 'Pleasure';
      }

      return value;
    },
    comment: 'Primary purpose of trip',
  })
  ftPurpose: string;

  @property({ type: Array, getType: () => TravelCountry })
  ftCountries: Array<TravelCountry> = [];

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Would your family accompany you on any of these trips',
  })
  ftWithFamily: boolean;

  @property({ rule: ['required'], comment: 'Details for family trips' })
  ftWithFamilyDetails: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment:
      'Have you used tobacco or other nicotine-containing products, including e-cigarettes and non-prescription smoking cessation aids',
  })
  isSmokester: boolean;

  @property({
    rule: ['required'],
    values: ['last12', 'last24', 'morethan24'],
    descriptions: function () {
      const policy = this.customer && this.customer.application && this.customer.application.primaryPolicy;
      return RiskFactors.getIsSmokerYearDescriptions(policy);
    },
    comment: 'How many months/years has it been since you quit smoking cigarettes',
  })
  isSmokerYear: string;

  @property({ comment: 'Use of cigarettes' })
  usesCigarettes: boolean;

  @property({ comment: 'Use of cigars' })
  usesCigars: boolean;

  @property({
    rule: ['required'],
    values: ['Yes', 'No'],
    descriptions: { Yes: 'Yes', No: 'No' },
    comment: 'Currently cigar smoker',
  })
  usesCigarsCurrently: string;

  @property({ comment: 'Using Other (Pipe, Chewing Tobacco, E-Cigarettes, Patch, Gum, Smoking Cessation Aids)' })
  usesPipe: boolean;

  @property({
    values: ['Yes', 'No'],
    descriptions: { Yes: 'Yes', No: 'No' },
    comment: 'Currently use other tobacco products',
  })
  currUsesOtherTobacco: string;

  @property({ rule: ['required', 'numericPositive'], comment: 'Avg. number of cigars smoked per month' })
  usesCigarsFrequency: number;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 100)',
    comment: 'Years since quit smoking cigarrettes',
  })
  quitCigarrettesPeriod: number;

  @property({
    rule: ['required'],
    values: ['last12', 'last24', 'morethan24'],
    descriptions: {
      last12: 'In the last 12 months',
      last24: 'Between 12 and 24 months ago',
      morethan24: 'More than 2 years ago',
    },
    comment: 'Last smoked cigars timeframe',
  })
  whenLastSmokedCigars: string;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Limited to 2 per month',
  })
  limit2CigarsPerMonth: boolean;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 100)', comment: 'Years since quit smoking cigars' })
  quitCigarsPeriod: number;

  @property({ rule: ['required', 'numeric'], validate: 'isWithin(0, 200)', comment: 'Cigars per month' })
  usedCigarsFrequency: number;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 100)',
    comment: 'Years since quit other tobacco products',
  })
  quitTobaccoPeriod: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Used prescription meds to assist with smoking cessation',
  })
  useTobaccoCessationMeds: boolean;

  @property({ comment: 'Uses marijuana' })
  usesMarijuana: boolean;

  @property({
    values: ['last12', 'last24', 'morethan24'],
    descriptions: {
      last12: 'In the last 12 months',
      last24: 'Between 12 and 24 months ago',
      morethan24: 'More than 2 years ago',
    },
    comment: 'The last time marijuana was used',
  })
  whenLastUsedMarijuana: string;

  @property({
    values: ['recreational_usage', 'medically_prescribed', 'both'],
    descriptions: {
      recreational_usage: 'Recreational Usage',
      medically_prescribed: 'Medically Prescribed',
      both: 'Both',
    },
    comment: 'Use marijuana recreationally or medically prescribed',
  })
  recreationalOrMedicalMarijuanaUser: string;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 7)',
    comment: 'Number of days per week marijuana is used',
  })
  usesMarijuanaFrequency: number;

  @property({ comment: 'Medical condition marijuana prescribed for' })
  medicinalMarijuanaCondition: string;

  @property({ comment: 'Information about the prescribing doctor' })
  medicinalMarijuanaDiagnosis: DiagnosisShort;

  @property({ comment: 'Information about the prescribing doctor' })
  hashimotoDiagnosis: DiagnosisShort;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Consumes more than 3 alcoholic drinks per day',
  })
  useAlcohol: boolean;

  @property({
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Consumes more than 5 or 6 alcoholic drinks in a day',
  })
  useAlcoholBinge: boolean;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Are you currently pregnant',
  })
  isPregnant: boolean;

  @property({
    rule: ['required'],
    values: ['Lost', 'Gained', 'NoIhavenot'],
    descriptions: function () {
      const policy = this.customer && this.customer.application && this.customer.application.primaryPolicy;
      return RiskFactors.getWeightLossDescriptions();
    },
    comment: 'Whether weight has been gained or lost',
  })
  weightLoss: string;

  @property({
    rule: ['required'],
    values: function () {
      const values = ['Exercise', 'Diet'];

      if (this.customer.application.primaryPolicy.insured.person.gender === '2') {
        values.push('ChildBirth');
        if (Utils.isCoverpathPlatform(this.customer.application.primaryPolicy.getChannelCode())) {
          values.push('Pregnancy');
        }
      }

      if (Utils.isCoverpathPlatform(this.customer.application.primaryPolicy.getChannelCode())) {
        values.push('Surgery');
      }

      values.push('Unknown');

      PubSub.publish('customer_application.primaryPolicy_insured_riskfactors_weightLossFromExcercise_changed', '');
      SubjectManager.publish(
        'customer_application.primaryPolicy_insured_riskfactors_weightLossFromExcercise_changed',
        ''
      );

      return values;
    },
    descriptions: {
      Exercise: 'Exercise',
      Diet: 'Diet',
      ChildBirth: 'Childbirth',
      Surgery: 'Surgery',
      Pregnancy: 'Pregnancy',
      Unknown: 'Unknown',
    },
    comment: 'Weight loss/gain due to:',
  })
  weightLossFromExcercise: string;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(11, 200)',
    comment: 'In the last 12 months alone, how much weight did you [lose/gain]',
  })
  weightLossFromDiet: number;

  @property({
    rule: ['required'],
    values: ['true', 'false'],
    descriptions: { true: 'Yes', false: 'No' },
    comment: 'Weight loss/gain more than 10 lbs in the past year',
  })
  hasWeightChangedBy10: boolean;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 99)',
    comment: 'How many alcoholic beverage per week',
  })
  howManyAlcohol: number;

  @property({ comment: "I don't drink alcohol" })
  dontDrinkAlcohol: boolean;

  // Rare only
  @property({
    rule: ['required'],
    values: ['years_ago', 'within_last_year', 'never'],
    descriptions: { years_ago: '__ years ago', within_last_year: 'Within the last year', never: 'Never' },
    comment: 'Last time used tobacco or other nicotine-containing products',
  })
  whenLastSmoked: string;

  @property({
    toServer: false,
    toClient: false,
    comment: 'Dummy Column - Keeps DTC and CP in sync',
  })
  whenLastSmokedAnswerChanges: number;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(1, 30, false)',
    comment: 'Number of years since last smoked',
  })
  yearsAgoLastSmoked: number;

  @property({
    rule: ['required'],
    values: ['last12', 'last24', 'morethan24'],
    descriptions: {
      last12: 'In the last 12 months',
      last24: 'Between 12 and 24 months ago',
      morethan24: 'More than 2 years ago',
    },
    comment: 'Last time smoked cigarettes',
  })
  whenLastSmokedCigarettes: string;

  @property({
    rule: ['required'],
    values: ['years_ago', 'within_last_year', 'never'],
    descriptions: { years_ago: '__ years ago', within_last_year: 'Within the last year', never: 'Never' },
    comment: 'Last time tobacco, e-cigarettes, other nicotine-containing products, or marijuana was used',
  })
  whenLastSmokedOrUsedMarijuana: string;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(1, 30, false)',
    comment: 'Number of years since last smoked or used marijuana',
  })
  yearsAgoLastSmokedOrUsedMarijuana: number;

  @property({
    rule: ['required', 'numeric'],
    validate: 'isWithin(0, 31)',
    comment: 'Number of times per month more than 3 alcoholic drinks in one day are consumed',
  })
  bingeDrinkingMonthly: number;

  @property()
  hadNoneOfTheAboveTreatment: boolean;

  @property()
  occupationFactors: OccupationFactors;

  @property({ getType: () => Customer })
  customer: Customer;

  @property({ getType: () => Applicant, persist: false })
  insured: Applicant;

  constructor() {
    super();
  }

  static new(customer) {
    return new RiskFactors().init(customer);
  }

  static getWeightLossDescriptions() {
    return {
      Lost: 'Lost',
      Gained: 'Gained',
      NoIhavenot: 'Neither',
    };
  }

  static getIsSmokerYearDescriptions(policy: Policy) {
    const descriptions = {
      ICC: {
        last12: 'in the last 12 months',
        last24: 'between 12 and 24 months ago',
        morethan24: 'more than 2 years ago',
      },
      nonICC: {
        last12: 'In the last 12 months',
        last24: 'Between 12 and 24 months ago',
        morethan24: 'More than 24 months ago',
      },
    };
    return policy && policy.isICCApplication() ? descriptions.ICC : descriptions.nonICC;
  }

  init(customer) {
    this.customer = customer;
    this.occupationFactors = OccupationFactors.new(customer);
    return this;
  }

  howManyAlcoholTrigger() {
    this.useAlcohol = this.howManyAlcohol > 21;
  }

  whenLastSmokedTrigger() {
    if (this.whenLastSmoked === 'within_last_year') {
      this.isSmokester = true;
      this.isSmokerYear = 'last12';
      this.yearsAgoLastSmoked = undefined;
    } else if (this.whenLastSmoked === 'years_ago') {
      this.isSmokester = true;
    } else {
      this.isSmokester = false;
      this.yearsAgoLastSmoked = undefined;
      this.yearsAgoLastSmokedTrigger();
    }
  }

  yearsAgoLastSmokedTrigger() {
    if (this.yearsAgoLastSmoked > 2) {
      this.isSmokerYear = 'morethan24';
    } else if (this.yearsAgoLastSmoked > 1) {
      this.isSmokerYear = 'last24';
    } else if (this.yearsAgoLastSmoked > 0) {
      this.isSmokerYear = 'last12';
    } else {
      this.isSmokerYear = null;
    }
  }

  avBigGameTrigger() {
    /**
     * This trigger is to add an initial BigGameCountry object to the avBigGameCountries array
     * avBigGameCountries is not used in Haven (for now)
     * and because no Haven metadata question references the property,
     * validateServerIncomingProperty function in Part2ValidatingController.ts returns "No candidates found" error
     * so only setup the avBigGameCountries array for Coverpath, MMD, and LifeBridge
     * Utils.isCoverpathPlatform will return true for Coverpath, MMD, and LifeBridge
     *
     * if Haven adds a question to use avBigGameCountries, remove the check for isCoverpathPlatform
     */
    if (Utils.isCoverpathPlatform(this.customer.application.primaryPolicy.getChannelCode())) {
      if (this.avBigGame) {
        if (this.avBigGameCountries.length === 0) {
          this.avBigGameCountries.push(BigGameCountry.new(this.customer));
        }
      } else {
        this.avBigGameCountries = [];
      }
    }
  }

  avOtherTrigger() {
    if (this.avOther) {
      if (this.avOtherDetail.length === 0) {
        this.avOtherDetail.push(Avocation.new(this.customer));
      }
    } else {
      this.avOtherDetail = [];
    }
  }

  mvAccidentAtFaultTrigger() {
    if (this.mvAccidentAtFault) {
      if (this.mvAccidentAtFaults.length === 0) {
        this.mvAccidentAtFaults.push(AccidentAtFault.new(this.customer));
      }
    } else {
      this.mvAccidentAtFaults = [];
    }
  }

  mvSpeedingTrigger() {
    if (this.mvSpeeding) {
      if (this.mySpeedingTickets.length === 0) {
        this.mySpeedingTickets.push(SpeedingTicket.new(this.customer));
      }
    } else {
      this.mySpeedingTickets = [];
    }
  }

  mbRecklessTrigger() {
    if (this.mbReckless) {
      if (this.mbRecklessTickets.length === 0) {
        this.mbRecklessTickets.push(RecklessTicket.new(this.customer));
      }
    } else {
      this.mbRecklessTickets = [];
    }
  }

  mvOtherTrigger() {
    if (this.mvOther) {
      if (this.mvOthers.length === 0) {
        this.mvOthers.push(OtherMovingViolation.new(this.customer));
      }
    } else {
      this.mvOthers = [];
    }
  }

  mvDrivingWhileSuspendedTrigger() {
    if (this.mvDrivingWhileSuspended) {
      if (this.mvDrivingWhileSuspendedCons.length === 0) {
        this.mvDrivingWhileSuspendedCons.push(DrivingWhileSuspendedCon.new(this.customer));
      }
    } else {
      this.mvDrivingWhileSuspendedCons = [];
    }
  }

  movingViolationCount() {
    return (
      this.mvAccidentAtFaults.length +
      this.mySpeedingTickets.length +
      this.mbRecklessTickets.length +
      this.mvOthers.length +
      this.mvDrivingWhileSuspendedCons.length
    );
  }

  yearsSinceLastMovingViolation() {
    let yrsSinceLastViol = yrsSinceLastViolation(this.mvAccidentAtFaults, 'mvAccidentWhen', 99);

    yrsSinceLastViol = yrsSinceLastViolation(this.mySpeedingTickets, 'mvSpeedingDate', yrsSinceLastViol);
    yrsSinceLastViol = yrsSinceLastViolation(this.mbRecklessTickets, 'mvRecklessDate', yrsSinceLastViol);
    yrsSinceLastViol = yrsSinceLastViolation(this.mvOthers, 'mvOtherWhen', yrsSinceLastViol);

    return yrsSinceLastViolation(this.mvDrivingWhileSuspendedCons, 'mvDWSConvictionDate', yrsSinceLastViol);

    function yrsSinceLastViolation(viols, whenProp, yrsSinceLastViol) {
      return viols.reduce(function (yrsSinceLastV, viol) {
        const yrsSinceViol = yrs(viol[whenProp]);
        return yrsSinceViol < yrsSinceLastV ? yrsSinceViol : yrsSinceLastV;
      }, yrsSinceLastViol);
    }

    function yrs(opt) {
      switch (opt) {
        case '0-1years':
          return 1;

        case '1-2years':
          return 2;

        case '2-3years':
          return 3;

        default:
          return 5;
      }
    }
  }

  yearsSinceLastDuiViolation() {
    return this.mvDUI ? yrs(this.mvLastDUI) : 99;

    //TODO: code duplication! linter made me do it..
    function yrs(opt) {
      switch (opt) {
        case '0-1years':
          return 1;

        case '1-2years':
          return 2;

        case '2-3years':
          return 3;

        default:
          return 5;
      }
    }
  }

  isSmokesterTrigger() {
    // Reset waiver premium
    this.customer.application.primaryPolicy.waiverPremium = null;
  }

  isSmokerYearTrigger() {
    // Reset waiver premium
    this.customer.application.primaryPolicy.waiverPremium = null;
  }

  getTobaccoTypesAndFrequency() {
    const tobaccoTypes = [];

    if (this.usesCigarettes) {
      tobaccoTypes.push('Cigarettes');
    }

    if (this.usesCigars) {
      const freq = this.usesCigarsFrequency || this.usedCigarsFrequency;

      if (freq) {
        tobaccoTypes.push('Cigars' + ' (' + freq + '/month)');
      } else {
        tobaccoTypes.push('Cigars');
      }
    }

    if (this.usesPipe) {
      tobaccoTypes.push('Other');
    }

    return tobaccoTypes.join(', ');
  }

  isSmokerTimeFrame() {
    return this.isSmokester ? this.isSmokerYear : 'never';
  }

  ftBenignTrigger() {
    if (!this.customer.application.primaryPolicy.is2020CoverpathOrMMD()) {
      this.initTravelCountries();
    }
  }

  ftDurationTrigger() {
    if (!this.customer.application.primaryPolicy.is2020CoverpathOrMMD()) {
      this.initTravelCountries();
    }
  }

  anticipatingForeignTravelTrigger() {
    if (this.customer.application.primaryPolicy.is2020CoverpathOrMMD()) {
      if (this.anticipatingForeignTravel === 'Yes') {
        if (this.ftCountries.length === 0) {
          this.ftCountries.push(TravelCountry.new(this.customer));
        }
      } else {
        this.ftCountries = [];
      }
    }
  }

  initTravelCountries() {
    const riskFactors = this.customer.application.primaryPolicy.insured.riskFactors;

    if (riskFactors.ftBenign === false || riskFactors.ftDuration > 90) {
      if (riskFactors.ftCountries.length === 0) {
        riskFactors.ftCountries.push(TravelCountry.new(this.customer));
      }
    } else {
      riskFactors.ftCountries = [];
    }
  }

  isForeignTravelAnticipated(): boolean {
    return (
      this.customer.application.primaryPolicy.insured.person.residencyStatus === 'residentAlien' ||
      this.anticipatingForeignTravel === 'Yes'
    );
  }

  isForeignTravelAnticipatedEs() {
    return this.customer.application.primaryPolicy.isSpanishSubmission() && this.isForeignTravelAnticipated();
  }

  isMilitary(): boolean {
    return !!this.armedForces;
  }

  isAviator(): boolean {
    return !!this.isPilot;
  }

  isAviatorEs(): boolean {
    return this.customer.application.primaryPolicy.isSpanishSubmission() && this.isAviator();
  }

  hasRiskyAvocations(): boolean {
    return !!this.customer.application.primaryPolicy.insured.riskFactors.hasAvocations;
  }

  hasRiskyAvocationsEs(): boolean {
    return this.customer.application.primaryPolicy.isSpanishSubmission() && this.hasRiskyAvocations();
  }

  getLastFlightDate() {
    return Utils.dateToMY(this.lastFlightDate);
  }

  getSpecificFlyingDetailsForDocusign() {
    let details = '';

    if (this.flyOutsideUSDetails) {
      details += 'Applicant flights outside the U.S.: \n' + this.flyOutsideUSDetails + '\n';
    }

    if (this.ratingOther) {
      details += 'Applicant other ratings attained: \n' + this.ratingOther + '\n';
    }

    if (this.flyTypeOtherStr) {
      details += "Applicant nature of flying 'other': \n" + this.flyTypeOtherStr + '\n';
    }

    if (this.flyAircraftTypeOtherDetails) {
      details += 'Applicant additional aircraft flown: \n' + this.flyAircraftTypeOtherDetails + '\n';
    }

    if (this.militaryDutiesOther) {
      details += 'Applicant other military duties: \n' + this.militaryDutiesOther + '\n';
    }

    if (this.militaryFlyAircraftTypeOther) {
      details += 'Applicant pilots other military craft details: \n' + this.militaryFlyAircraftTypeOtherDetails + '\n';
    }

    return details;
  }

  getMilitaryHoursTotal() {
    if (this.pilotCategory === 'Military' && this.pilot === 'pilot') {
      return this.pilotTotalHrs;
    }
    return null;
  }

  getCivHoursTotal() {
    if (this.pilotCategory === 'Private_Student' && this.pilot === 'pilot') {
      return this.pilotTotalHrs;
    }
    return null;
  }

  getCommHoursTotal() {
    if (this.pilotCategory === 'Commercial' && this.pilot === 'pilot') {
      return this.pilotTotalHrs;
    }
    return null;
  }

  getMilitaryHoursLast12() {
    if (this.pilotCategory === 'Military' && this.pilot === 'pilot') {
      return this.pilotLast12MonthsHrs;
    }
    return null;
  }

  getCivHoursLast12() {
    if (this.pilotCategory === 'Private_Student' && this.pilot === 'pilot') {
      return this.pilotLast12MonthsHrs;
    }
    return null;
  }

  getCommHoursLast12() {
    if (this.pilotCategory === 'Commercial' && this.pilot === 'pilot') {
      return this.pilotLast12MonthsHrs;
    }
    return null;
  }

  getMilitaryHoursNext12() {
    if (this.pilotCategory === 'Military' && this.pilot === 'pilot') {
      return this.pilotNext12MonthsHrs;
    }
    return null;
  }

  getCivHoursNext12() {
    if (this.pilotCategory === 'Private_Student' && this.pilot === 'pilot') {
      return this.pilotNext12MonthsHrs;
    }
    return null;
  }

  getCommHoursNext12() {
    if (this.pilotCategory === 'Commercial' && this.pilot === 'pilot') {
      return this.pilotNext12MonthsHrs;
    }
    return null;
  }

  getCivCrewLast12() {
    if (this.pilotCategory === 'Private_Student' && this.pilot === 'crew') {
      return this.crewMemberLast12MonthsHrs;
    }
    return null;
  }

  getCommCrewLast12() {
    if (this.pilotCategory === 'Commercial' && this.pilot === 'crew') {
      return this.crewMemberLast12MonthsHrs;
    }
    return null;
  }

  getMilCrewLast12() {
    if (this.pilotCategory === 'Military' && this.pilot === 'crew') {
      return this.crewMemberLast12MonthsHrs;
    }
    return null;
  }

  getCivCrewNext12() {
    if (this.pilotCategory === 'Private_Student' && this.pilot === 'crew') {
      return this.crewMemberNext12MonthsHrs;
    }
    return null;
  }

  getCommCrewNext12() {
    if (this.pilotCategory === 'Commercial' && this.pilot === 'crew') {
      return this.crewMemberNext12MonthsHrs;
    }
    return null;
  }

  getMilCrewNext12() {
    if (this.pilotCategory === 'Military' && this.pilot === 'crew') {
      return this.crewMemberNext12MonthsHrs;
    }
    return null;
  }

  getAvoUnderwater() {
    return Boolean(this.avScubaDiving);
  }

  getAvoClimbing() {
    return Boolean(this.avMountainClimbing);
  }

  getAvoAerials() {
    return Boolean(this.avBalloning || this.avSkyDiving || this.avHangGliding || this.avBungee);
  }

  getAvoRacing() {
    return Boolean(this.avAutoRacing || this.avMotorcycleRacing || this.avMotorboatRacing);
  }

  getRacingDetails() {
    if (this.getAvoRacing) {
      return 'see additional details sheet';
    }
  }

  getAvoRock() {
    if (this.getAvoClimbing) {
      return !this.avMCRated;
    }
    return false;
  }

  getAvoMountain() {
    return this.getAvoClimbing && this.avMCRated;
  }

  getAvoHangGlide() {
    return Boolean(this.avHangGliding);
  }

  getAvoHangGlideHeight() {
    if (this.avHangGlidingLow === 'Yes') {
      return 'Under 500 feet altitude';
    } else if (this.avHangGlidingLow === 'No') {
      return 'Over 500 feet altitude';
    } else {
      return 'Unsure';
    }
  }

  getAvoHangGlideSoar(): boolean {
    return this.avHangGlidingLow === 'No';
  }

  getAvoNumTimesHangOrGlide() {
    if (this.avHangGliding || this.avSkyDiving) {
      if (this.avHangGlidingOften || this.avSKDInfrequent) {
        return 'more than 25';
      } else {
        return 'fewer than 25';
      }
    }

    return null;
  }

  medicalConditionExistsByType(
    conditionType: ConditionType | CancerType | MedicalConditionType | SubstanceType
  ): boolean {
    return !!this.getMedicalConditionByType(conditionType);
  }

  getMedicalConditionByType(
    conditionType: ConditionType | CancerType | MedicalConditionType | SubstanceType
  ): MedicalCondition {
    if (conditionType && this.medicalConditions && this.medicalConditions.length > 0) {
      return this.medicalConditions.find(
        (medicalCondition) =>
          medicalCondition &&
          (medicalCondition.conditionType === conditionType || medicalCondition.subConditionType === conditionType)
      );
    }

    return undefined;
  }

  setMedicalConditionAndTreatment(
    medicalCondition: MedicalCondition,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    subConditionType?: CancerType | SubstanceType,
    isSetOnParent?: boolean,
    hasDiagnosis?: boolean,
    treatmentType?: TreatmentType,
    testType?: TestType,
    resultsFindings?: ResultsFindings,
    resultsType?: QualitativeResult,
    instantiateDiagnosis?: boolean,
    forceSetTreatment?: boolean
  ) {
    if (isSetOnParent && (hasDiagnosis || treatmentType || testType)) {
      // default treatment type for cancer
      if (!treatmentType && !testType && conditionType === ConditionType.OLI_MEDCOND_CANCER) {
        treatmentType = TreatmentType.OLI_UNKNOWN;
      }

      // create new medical condition, if needed
      if (!medicalCondition && conditionType) {
        medicalCondition = MedicalCondition.newRiskFactors(
          this,
          conditionType,
          subConditionType,
          undefined,
          forceSetTreatment ? hasDiagnosis : true
        );
        if (medicalCondition) {
          this.medicalConditions.push(medicalCondition);
        }
      }
      // find med cod index
      let medCondIndex: number = -1;
      this.medicalConditions.forEach((medCond, index) => {
        if (medCond === medicalCondition) {
          medCondIndex = index;
        }
      });

      if (forceSetTreatment && this.medicalConditions[medCondIndex]) {
        medicalCondition.optionSelected = hasDiagnosis;
        this.medicalConditions[medCondIndex].optionSelected = hasDiagnosis;
      }

      // reset condition if diagnosis is false for non misc conditions, otherwise
      // clear and re-insert treatment and preventions (as needed)
      const setTreatment =
        (medicalCondition && medicalCondition.conditionType === MedicalConditionType.MISCELLANEOUS_CONDITION) ||
        forceSetTreatment;
      if (this.medicalConditions[medCondIndex] && !hasDiagnosis && !setTreatment) {
        const isCoverpathPlatform: boolean = Utils.isCoverpathPlatform(
          this.customer.application.primaryPolicy.getChannelCode()
        );
        this.medicalConditions[medCondIndex] = isCoverpathPlatform
          ? this.setMedicalConditiontoDefaultOnServer(this.medicalConditions[medCondIndex])
          : this.setMedicalConditiontoDefault(this.medicalConditions[medCondIndex]);
      } else {
        let medicalTreat: MedicalTreatment;
        let medicalPrevention: MedicalPrevention;
        if (this.medicalConditions[medCondIndex] && treatmentType) {
          const medTreatment = this.getMedicalTreatmentByType(
            this.medicalConditions[medCondIndex],
            this.medicalConditions[medCondIndex].conditionType,
            treatmentType
          );
          if (medTreatment && !instantiateDiagnosis) {
            this.medicalConditions[medCondIndex].removeMedicalTreatment(
              treatmentType,
              medTreatment.treatmentDesc,
              medTreatment.treatmentFrequencyMode,
              medTreatment.timePeriod
            );
          }
          medicalTreat = MedicalTreatment.new(this, this.medicalConditions[medCondIndex], hasDiagnosis, treatmentType);
          if (instantiateDiagnosis) {
            medicalTreat.details = DiagnosisShort.new(this.customer);
          }
          this.medicalConditions[medCondIndex].medicalTreatments.push(medicalTreat);
        }
        if (this.medicalConditions[medCondIndex] && (testType || resultsFindings || resultsType)) {
          const medPrevention = this.getMedicalTestByType(
            this.medicalConditions[medCondIndex],
            this.medicalConditions[medCondIndex].conditionType,
            testType,
            resultsFindings
          );
          if (medPrevention && !instantiateDiagnosis) {
            this.medicalConditions[medCondIndex].removeMedicalPrevention(
              resultsFindings ? resultsFindings : medPrevention.resultsFindings,
              testType,
              resultsType ? resultsType : medPrevention.resultsType
            );
          }
          medicalPrevention = MedicalPrevention.new(
            this,
            this.medicalConditions[medCondIndex],
            hasDiagnosis,
            testType,
            undefined,
            resultsFindings,
            resultsType
          );
          this.medicalConditions[medCondIndex].medicalPreventions.push(medicalPrevention);
        }
      }
    } else {
      // if misc condition then remove treatments and preventions otherwise reset entire med condition
      if (!isSetOnParent || !hasDiagnosis) {
        this.medicalConditions.forEach((medicalConditionObj, index) => {
          if (
            medicalConditionObj.conditionType === conditionType &&
            (medicalConditionObj.subConditionType === subConditionType ||
              (!subConditionType && !medicalConditionObj.subConditionType))
          ) {
            if (medicalConditionObj.conditionType === MedicalConditionType.MISCELLANEOUS_CONDITION) {
              if (
                testType &&
                this.medicalConditions[index].medicalPreventions &&
                this.medicalConditions[index].medicalPreventions.length > 0
              ) {
                this.medicalConditions[index].medicalPreventions = this.medicalConditions[
                  index
                ].medicalPreventions.filter(
                  (medicalPrevention) =>
                    medicalPrevention &&
                    !(
                      medicalPrevention.testType === testType &&
                      medicalPrevention.resultsFindings ===
                        (resultsFindings ? resultsFindings : medicalPrevention.resultsFindings) &&
                      medicalPrevention.resultsType === (resultsType ? resultsType : medicalPrevention.resultsType)
                    )
                );
              }
              if (
                treatmentType &&
                this.medicalConditions[index].medicalTreatments &&
                this.medicalConditions[index].medicalTreatments.length > 0
              ) {
                this.medicalConditions[index].medicalTreatments = this.medicalConditions[
                  index
                ].medicalTreatments.filter(
                  (medicalTreatment) => medicalTreatment && medicalTreatment.treatmentType !== treatmentType
                );
              }
            } else {
              const isCoverpathPlatform: boolean = Utils.isCoverpathPlatform(
                this.customer.application.primaryPolicy.getChannelCode()
              );
              this.medicalConditions[index] = isCoverpathPlatform
                ? this.setMedicalConditiontoDefaultOnServer(this.medicalConditions[index])
                : this.setMedicalConditiontoDefault(this.medicalConditions[index]);
            }
          }
        });
      }
    }
  }

  setMedicalCondition(
    medicalCondition: MedicalCondition,
    conditionType: ConditionType | SubstanceType,
    subConditionType?: CancerType | SubstanceType,
    isSetOnParent?: boolean,
    hasDiagnosis?: boolean,
    treatmentType?: TreatmentType,
    setOptionsSelected?: boolean
  ) {
    if (!medicalCondition && isSetOnParent && (hasDiagnosis || setOptionsSelected)) {
      this.medicalConditions.push(
        MedicalCondition.newRiskFactors(
          this,
          conditionType,
          subConditionType,
          treatmentType,
          setOptionsSelected ? hasDiagnosis : undefined
        )
      );
    } else {
      this.medicalConditions.forEach((medicalConditionObj, index) => {
        if (
          medicalConditionObj.conditionType === conditionType &&
          (medicalConditionObj.subConditionType === subConditionType ||
            (!subConditionType && !medicalConditionObj.subConditionType))
        ) {
          if (!setOptionsSelected) {
            const isCoverpathPlatform: boolean = Utils.isCoverpathPlatform(
              this.customer.application.primaryPolicy.getChannelCode()
            );
            this.medicalConditions[index] = isCoverpathPlatform
              ? this.setMedicalConditiontoDefaultOnServer(this.medicalConditions[index])
              : this.setMedicalConditiontoDefault(this.medicalConditions[index]);
          } else {
            this.medicalConditions[index].optionSelected = hasDiagnosis;
          }
        }
      });
    }
  }

  resetMedicalConditionRecords() {
    if (this.medicalConditions.length > 0) {
      this.medicalConditions = this.medicalConditions.filter(
        (medicalConditionObj) => medicalConditionObj && medicalConditionObj.conditionType
      );
    }
  }

  medicalTreatmentExistsByType(
    medicalConditon: MedicalCondition,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    treatmentType: TreatmentType
  ): boolean {
    const treatment = this.getMedicalTreatmentByType(medicalConditon, conditionType, treatmentType);
    return treatment && treatment.optionSelected;
  }

  medicalTestExistsByType(
    medicalConditon: MedicalCondition,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    testType: TestType,
    resultsFindings?: ResultsFindings,
    resultsType?: QualitativeResult
  ): boolean {
    const prevention = this.getMedicalTestByType(
      medicalConditon,
      conditionType,
      testType,
      resultsFindings,
      resultsType
    );
    return prevention && prevention.optionSelected;
  }

  getMedicalTreatmentByType(
    medicalCondition: MedicalCondition,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    treatmentType: TreatmentType
  ): MedicalTreatment {
    const treatments = this.getAllMedicalTreatmentsByType(medicalCondition, conditionType, treatmentType);
    return treatments && treatments[0];
  }

  getAllMedicalTreatmentsByType(
    medicalCondition: MedicalCondition,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    treatmentType: TreatmentType
  ): MedicalTreatment[] {
    if (
      conditionType &&
      treatmentType &&
      medicalCondition &&
      medicalCondition.medicalTreatments &&
      medicalCondition.conditionType === conditionType &&
      medicalCondition.medicalTreatments.length > 0
    ) {
      return medicalCondition.medicalTreatments.filter(
        (medicalTreatment) => medicalTreatment && medicalTreatment.treatmentType === treatmentType
      );
    }

    return undefined;
  }

  getMedicalTestByType(
    medicalCondition: MedicalCondition,
    conditionType: ConditionType | MedicalConditionType | SubstanceType,
    testType: TestType,
    resultsFindings?: ResultsFindings,
    resultsType?: QualitativeResult
  ): MedicalPrevention {
    if (
      conditionType &&
      testType &&
      medicalCondition &&
      medicalCondition.medicalPreventions &&
      medicalCondition.conditionType === conditionType &&
      medicalCondition.medicalPreventions.length > 0
    ) {
      return medicalCondition.medicalPreventions.find(
        (medicalPrevention) =>
          medicalPrevention &&
          medicalPrevention.testType === testType &&
          medicalPrevention.resultsFindings ===
            (resultsFindings ? resultsFindings : medicalPrevention.resultsFindings) &&
          medicalPrevention.resultsType === (resultsType ? resultsType : medicalPrevention.resultsType)
      );
    }

    return undefined;
  }

  getAvoSkyDiving(): boolean {
    return Boolean(this.avSkyDiving);
  }

  getAvoBoatRacing(): boolean {
    return Boolean(this.avMotorboatRacing);
  }

  getAvoCycleRacing(): boolean {
    return Boolean(this.avMotorcycleRacing);
  }

  getAvoAutoRacing(): boolean {
    return Boolean(this.avAutoRacing);
  }

  getAvoRaceSpecific() {
    if (this.getAvoBoatRacing || this.avMotorcycleRacing || this.avAutoRacing) {
      return 'See Additional Details';
    }

    return null;
  }

  getSeeOtherDetails() {
    if (this.getAvoDetails.length > 24) {
      return 'SEE OTHER DETAILS ON SUPPLEMENT FORM';
    }

    return false;
  }

  getAvoDetails() {
    let details = 'Form A3320: Avocations \n';
    const pip = 'Proposed insured participates';

    if (this.avHangGlidingLow === 'NotSure') {
      details += pip + " in potential hang glide soaring, answered 'Unsure'" + '\n';
    }

    if (this.avAutoRacing) {
      if (this.avARATV) {
        details += pip + " in auto racing includes ATV's" + '\n';
      }

      if (this.avARATV3Wheel) {
        details += "proposed insured's auto racing includes 3 wheeled ATV's" + '\n';
      }

      if (this.avARCrash) {
        details += "proposed insured's auto racing includes Auto Crash racing" + '\n';

        if (this.avARCDiveBomber) {
          details += 'These races include Diver Bomber, Rollovers, and T-Bones' + '\n';
        }

        if (this.avARCDemolition) {
          details += "These races include Demolition, Destruction Derby, Figure '8' Demolition" + '\n';
        }
      }

      if (this.avARGoCart) {
        if (this.avARGCSprint) {
          details += 'Proposed insured participates in Go Kart Racing of the Sprint style' + '\n';
        }

        if (this.avARGCEnduro) {
          details += 'Proposed insured participates in Go Kart Racing of the Enduro style' + '\n';
        }

        if (this.avARGCFormula) {
          details += 'Proposed insured participates in Go Kart Racing of the Formula Kart Experiment style' + '\n';
        }
      }

      if (this.avARGCMidget) {
        details += 'Proposed insured participates in Midget Car Racing' + '\n';
      }

      if (this.avARGCMini) {
        details += 'Proposed insured participates in Mini car racing' + '\n';
      }

      if (this.avAROffRoad) {
        if (this.avARORDesert) {
          details += "Proposed insured races 'off road Desert' (incl. Baja, Dune, Trail) - U.S. Only";
        }

        if (this.avARORRally) {
          details += 'Proposed insured races long distance rally, US only';
        }
      }
    }

    if (this.avAROtherDetails) {
      details += this.avAROtherDetails + '\n';
    }

    if (this.avBalloning) {
      details += 'Proposed Insured participates in ballooning' + '\n';
    }

    if (this.avBigGame) {
      details += 'Proposed insured hunts big game' + '\n';
    }

    if (this.avBobsledding) {
      details += 'Proposed insured bobsleds' + '\n';
    }

    if (this.avBoxing) {
      details += 'Proposed insured boxes amateurly' + '\n';
    }

    if (this.avBoxingPro) {
      details += pip + 'in professional boxing' + '\n';
    }

    if (this.avBungee) {
      details += pip + 'in bungee jumping' + '\n';
    }

    if (this.avHeliSkiing) {
      details += pip + 'in Heli-skiing' + '\n';
    }

    if (this.avMartialArts) {
      details += pip + 'in martial arts, amateur or instructor' + '\n';
    }

    if (this.avMartialArtsPro) {
      details += pip + 'in professional martial arts competition' + '\n';
    }

    if (this.avMotorboatRacing) {
      details += pip + 'motorboat / powerboat racing' + '\n';
    }

    if (this.avMotorcycleRacing) {
      details += pip + 'races motorcycles ' + this.avMRType;

      if (this.avMRDrag) {
        details +=
          ' in drag races, dragster division racing, road, speedway, or tourist trophy racing, possibly outside of the U.S. \n';
      } else {
        details += '\n';
      }
    }

    if (this.avSkyDiving) {
      details +=
        pip +
        'in skydiving, ultralight, etc. Plans to do so in the next two years: ' +
        this.avSDSoon +
        '. Jumps fewer than 25 times a year: ' +
        this.avSKDInfrequent +
        'Limited to amateur, USPA, no baton passing, no delayed chute openings: ' +
        this.avSDAmateur;
    }

    if (this.avScubaDiving) {
      details +=
        pip + 'in scuba dives ' + this.avSDShallow
          ? 'under'
          : 'over' +
            ' 100 feet in depth. With limited certifications: ' +
            this.avSDBasicCert +
            '. Over 50 times a year: ' +
            this.avSDInfrequent +
            '\n';
    }

    if (this.avOther) {
      for (let i = 0; i < this.avOtherDetail.length; i++) {
        const otherDetail = this.avOtherDetail[i];
        if (otherDetail) {
          details += `${pip}${otherDetail.type} done this frequently: ${otherDetail.frequency}`;
        }
      }
    }

    return details;
  }

  getListOfAvocationsDescriptions() {
    const avocations = [];

    if (this.avAutoRacing) {
      avocations.push('Auto Racing');
    }

    if (this.avBalloning) {
      avocations.push('Ballooning');
    }

    if (this.avBigGame) {
      avocations.push('Big game hunting');
    }

    if (this.avBobsledding) {
      avocations.push('Bobsledding (Professional)');
    }

    if (this.avBoxing) {
      avocations.push('Boxing (Amateur)');
    }

    if (this.avBoxingPro) {
      avocations.push('Boxing (Professional)');
    }

    if (this.avBungee) {
      avocations.push('Bungee Jumping');
    }

    if (this.avHangGliding) {
      avocations.push('Hang gliding');
    }

    if (this.avHeliSkiing) {
      avocations.push('Heli skiing');
    }

    if (this.avMartialArts) {
      avocations.push('Martial Arts (Amateur or Instructor)');
    }

    if (this.avMartialArtsPro) {
      avocations.push('Martial Arts (Professional Competition)');
    }

    if (this.avMotorboatRacing) {
      avocations.push('Motorboat / powerboat racing');
    }

    if (this.avMotorcycleRacing) {
      avocations.push('Motorcycle racing');
    }

    if (this.avMountainClimbing) {
      avocations.push('Mountain climbing');
    }

    if (this.avScubaDiving) {
      avocations.push('Scuba Diving');
    }

    if (this.avSkyDiving) {
      avocations.push('Skydiving, parachuting, ultralight, etc');
    }

    if (this.avUltralightSoaring) {
      avocations.push('Ultralight / soaring');
    }

    if (this.avSnowmobileRacing) {
      avocations.push('Snowmobile racing');
    }

    if (this.avOther) {
      this.avOtherDetail.forEach(function (otherAv) {
        avocations.push('User-defined, Type: ' + otherAv.type + ', Frequency: ' + otherAv.frequency);
      });
    }

    return avocations;
  }

  getWhichCountriesFT() {
    if (this.anticipatingForeignTravel === 'Yes') {
      if (this.ftBenign && this.ftCountries.length === 0 && this.ftDuration <= 90) {
        return 'See section D';
      } else {
        return 'See Additional Supplement';
      }
    }

    return null;
  }

  getLengthOfTravelDays() {
    if (this.anticipatingForeignTravel === 'Yes') {
      if (this.ftCountries.length === 0 && this.ftDuration <= 90) {
        return 'See section D';
      } else {
        return 'See Additional Supplement';
      }
    }

    return null;
  }

  getAccompaniedFamily() {
    if (this.anticipatingForeignTravel === 'Yes') {
      return Boolean(this.ftWithFamily);
    }

    return null;
  }

  getFTPurpose() {
    if (this.anticipatingForeignTravel === 'Yes') {
      if (this.ftCountries.length === 0 && this.ftDuration <= 90) {
        return 'See section D';
      } else {
        return 'See Additional Supplement';
      }
    }
  }

  getFTdetails() {
    let details = '';

    if (
      this.anticipatingForeignTravel === 'Yes' &&
      this.ftBenign === true &&
      this.ftCountries.length === 0 &&
      this.ftDuration <= 90
    ) {
      details =
        'Travelling to Western Europe, Australia, Japan, South Korea, Canada, New Zealand or' +
        '\n Israel (excluding Gaza and the West Bank)' +
        '\n for ' +
        this.ftDuration +
        ' days. Reason: ' +
        this.ftPurpose;
    } else if ((this.anticipatingForeignTravel === 'Yes' && this.ftCountries.length > 0) || this.ftBenign === false) {
      details = 'See Additional Supplement';
    }

    details += '\n' + this.customer.application.primaryPolicy.insured.person.getResidencyAsset() + '\n';

    return details ? details : null;
  }

  getWeightLossDuetoExer() {
    return this.weightLossFromExcercise === 'Exercise';
  }

  getWeightLossDuetoChild() {
    return this.weightLossFromExcercise === 'ChildBirth';
  }

  getWeightLossDuetoDiet() {
    return this.weightLossFromExcercise === 'Diet';
  }

  getWeightLossDuetoUnknown() {
    return this.weightLossFromExcercise === 'Unknown';
  }

  getInsuredAlcoholBool() {
    return Number(this.mvDUIConvitions) > 0;
  }

  getOwnerPersonTestamentEstate() {
    return this.customer.application.primaryPolicy.ownerDelegate === 'ownerEstate';
  }

  getOwnerPersonTestamentInsured() {
    return this.customer.application.primaryPolicy.ownerDelegate !== 'ownerEstate';
  }

  getHivAddyPref() {
    return (
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'insured_address' ||
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress ===
        'insured_another_address'
    );
  }

  getHIVAddrName() {
    let addrName = '';
    const hivAddress = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;

    if (hivAddress === 'insured_address' || hivAddress === 'insured_another_address') {
      addrName = this.customer.application.primaryPolicy.insured.person.getFullName();
    } else if (hivAddress === 'pp_address') {
      addrName = this.customer.application.primaryPolicy.insured.personalHistory.primaryPhysicianName;
    } else if (hivAddress === 'pp_another_address') {
      addrName = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddressPPName;
    } else if (hivAddress === 'state_health') {
      addrName = 'Your State Insurance Department';
    }

    return addrName;
  }

  getHIVHomeAddrNameAndStreet() {
    return `${this.getHivHomeAddyName()} ${this.getHivAddyHomeStreet()}`;
  }

  getHivHomeAddyName() {
    if (this.getHivAddyPref()) {
      return this.customer.application.primaryPolicy.insured.person.getFullName();
    }

    return '';
  }

  getHivAddyHomeStreet() {
    let address = null;
    let line1 = null;

    if (this.getHivAddyPref()) {
      if (
        this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'insured_address'
      ) {
        line1 = this.customer.application.primaryPolicy.insured.address.line1 + ',' || '';
        address =
          this.customer.application.primaryPolicy.insured.address.street +
          ', ' +
          line1 +
          ' ' +
          this.customer.application.primaryPolicy.insured.address.city +
          ', ' +
          this.customer.application.primaryPolicy.insured.address.state +
          ' ' +
          this.customer.application.primaryPolicy.insured.address.zip;

        return this.formatHIVAddress(address);
      } else if (
        this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress ===
        'insured_another_address'
      ) {
        address = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddress;

        return this.formatHIVAddress(address);
      }
    }

    return '';
  }

  getHIVDRName() {
    if (
      this.getHivAddyPref() !== true &&
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress !== 'state_health'
    ) {
      return (
        this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddressPPName ||
        this.customer.application.primaryPolicy.insured.personalHistory.primaryPhysicianName ||
        ''
      );
    }
  }

  getHivDrAddyAndName() {
    let altAddress = null;

    if (this.getHivAddyPref() !== true) {
      if (this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_address') {
        altAddress = this.customer.application.primaryPolicy.insured.personalHistory.getPrimaryPhysicianAddress();
        return altAddress;
      } else {
        altAddress = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddress;
        return this.formatHIVAddress(altAddress);
      }
    }
  }

  formatHIVAddress(address) {
    let newAddress = '',
      newLine = 0;
    const regexp = '/(New)|(new)|(Rhode)|(rhode)|(North)|(north)|(South)|(south)|(West)|(west)/g';

    if (address && address.length > 70) {
      address = address.split(' ');

      for (let i = 0; i < address.length; i++) {
        if (i === address.length - 3 && address[i].search(regexp) !== -1) {
          newLine += 1;
          newAddress += ' \n ';
        } else if (i === address.length - 2 && newLine < 1 && address[i - 3].search(regexp) === -1) {
          newAddress += ' \n ';
        }
        newAddress += address[i] + ' ';
      }

      return newAddress;
    } else {
      return address;
    }
  }

  getHIVAddressType() {
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;
    const state = this.customer.application.primaryPolicy.insured.address.state;
    let type;

    if (hivPosAddy === 'pp_address' || hivPosAddy === 'pp_another_address') {
      type = 'pp';
    } else if (hivPosAddy === 'insured_address' || hivPosAddy === 'insured_another_address') {
      type = 'ins';
    } else if (hivPosAddy === 'state_health') {
      type = 'state';
    } else if (hivPosAddy === 'local_health') {
      type = 'local';
    } else {
      type = 'oth';
    }

    // Specific for IA
    if (state === 'IA') {
      if (hivPosAddy === 'pp_address') {
        type = 'pp';
      } else if (hivPosAddy === 'pp_another_address') {
        type = 'oth';
      } else {
        type = '';
      }
    }

    return type;
  }

  isHIVAddressTypePhysician(): boolean {
    return this.getHIVAddressType() === 'pp';
  }

  isHIVAddressTypeOther(): boolean {
    return this.getHIVAddressType() === 'oth';
  }

  isHIVAddressTypeState() {
    return this.getHIVAddressType() === 'state';
  }

  isHIVAddressTypeLocal() {
    return this.getHIVAddressType() === 'local';
  }

  isHIVAddressTypeStateOrLocal(): boolean {
    return this.isHIVAddressTypeLocal() || this.isHIVAddressTypeState();
  }

  getHIVAddressTypeOwner() {
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;
    const ownerState = this.customer.application.primaryPolicy.owner
      ? this.customer.application.primaryPolicy.ownerPersonResidentialAddress.state
      : '';
    let type;

    if (hivPosAddy === 'pp_address' || hivPosAddy === 'pp_another_address') {
      type = 'pp';
    } else if (hivPosAddy === 'insured_address' || hivPosAddy === 'insured_another_address') {
      type = 'ins';
    } else if (hivPosAddy === 'state_health') {
      type = 'state';
    } else {
      type = 'oth';
    }

    // Specific for IA
    if (ownerState === 'IA') {
      if (hivPosAddy === 'pp_another_address') {
        type = 'oth';
      } else {
        type = '';
      }
    }

    // Specific changes for VA
    if (ownerState === 'VA') {
      type = 'state';
    }

    return type;
  }

  getHIVConsentDrName() {
    const state = this.customer.application.primaryPolicy.insured.address.state;
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;
    let docName = '';
    // States requiring only primary/alternate physician mapping
    const physicianExemptions = ['AZ', 'IA', 'KY', 'ME', 'MO', 'WI', 'KS', 'LA', 'TX', 'KS', 'NE', 'CO'];

    // States requiring no mapping for state health doctor name
    const stateHealthExemptions = ['FL', 'DC', 'CA'];

    // States where HIV form can be sent to insured
    const insuredInclusion = ['RI', 'VA', 'NH'];

    if (hivPosAddy === 'pp_address') {
      docName = this.customer.application.primaryPolicy.insured.personalHistory.primaryPhysicianName;
    } else if (hivPosAddy === 'pp_another_address') {
      docName = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddressPPName;
    } else if (hivPosAddy === 'state_health') {
      docName = 'Your State Department of Health';
    } else if (hivPosAddy === 'noneoftheabove') {
      docName = '';
    }

    // Exceptions
    if (physicianExemptions.includes(state) && !['pp_address', 'pp_another_address'].includes(hivPosAddy)) {
      docName = '';
    } else if (
      !insuredInclusion.includes(state) &&
      ['insured_address', 'insured_another_address'].includes(hivPosAddy)
    ) {
      docName = '';
    } else if (stateHealthExemptions.includes(state) && hivPosAddy === 'state_health') {
      docName = '';
    }
    return docName;
  }

  getHIVConsentDrNameOwner() {
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;
    const ownerState = this.customer.application.primaryPolicy.owner
      ? this.customer.application.primaryPolicy.ownerPersonResidentialAddress.state
      : '';

    // States requiring only primary/alternate physician mapping
    const physicianExemptions = ['AZ', 'IA', 'KY', 'ME', 'MO', 'WI', 'KS', 'LA'];

    // States requiring no mapping for state health doctor name
    const stateHealthExemptions = ['FL', 'DC', 'CA', 'VT'];

    // States where HIV form can be sent to owner
    const insuredInclusion = ['RI', 'VA', 'VT'];

    let docName;

    if (hivPosAddy === 'pp_address') {
      docName = this.customer.application.primaryPolicy.insured.personalHistory.primaryPhysicianName;
    } else if (hivPosAddy === 'pp_another_address') {
      docName = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddressPPName;
    } else if (hivPosAddy === 'state_health') {
      docName = 'Your State Department of Health';
    } else {
      docName = this.customer.application.primaryPolicy.insured.person.getFullName();
    }

    // Exceptions
    if (physicianExemptions.includes(ownerState) && !['pp_address', 'pp_another_address'].includes(hivPosAddy)) {
      docName = '';
    } else if (
      !insuredInclusion.includes(ownerState) &&
      ['insured_address', 'insured_another_address'].includes(hivPosAddy)
    ) {
      docName = '';
    } else if (!stateHealthExemptions.includes(ownerState) && hivPosAddy === 'state_health') {
      docName = '';
    }
    return docName;
  }

  getHIVAddress() {
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;
    const state = this.customer.application.primaryPolicy.insured.address.state;
    const address = this.customer.application.primaryPolicy.insured.address;

    // States requiring only primary/alternate physician mapping
    const physicianExemptions = ['AZ', 'IA', 'KY', 'ME', 'MO', 'WI', 'KS', 'LA', 'TX', 'KS', 'NE', 'CO'];

    // States requiring no mapping for state health address
    const stateHealthExemptions = ['NH', 'PA', 'RI', 'OR', 'UT', 'VT', 'VA', 'WV', 'WA', 'FL', 'DC', 'CA'];

    // States where HIV form can be sent to insured
    const insuredInclusion = ['RI', 'VA', 'NH', 'VT'];

    let hivAddress;

    if (hivPosAddy === 'pp_address') {
      hivAddress = this.customer.application.primaryPolicy.insured.personalHistory.getPrimaryPhysicianAddress();
    } else if (
      hivPosAddy === 'pp_another_address' ||
      hivPosAddy === 'insured_another_address' ||
      hivPosAddy === 'other_healthcare' ||
      hivPosAddy === 'other'
    ) {
      hivAddress = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddress;
    } else if (hivPosAddy === 'insured_address') {
      hivAddress = this.getHivPositiveAddress(address);
    } else {
      hivAddress = 'State Department of Health';
    }

    // Exemptions
    if (physicianExemptions.includes(state) && !['pp_address', 'pp_another_address'].includes(hivPosAddy)) {
      hivAddress = '';
    } else if (stateHealthExemptions.includes(state) && hivPosAddy === 'state_health') {
      hivAddress = '';
    } else if (
      !insuredInclusion.includes(state) &&
      ['insured_address', 'insured_another_address'].includes(hivPosAddy)
    ) {
      hivAddress = '';
    }
    return hivAddress;
  }

  getHivPositiveAddress(insuredAddress?) {
    const address = insuredAddress
      ? insuredAddress
      : this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddressTerm2;
    const hivAddress =
      (address.street ? address.street + ', ' : '') +
      (address.line1 ? address.line1 + ', ' : '') +
      (address.city ? address.city : '') +
      (address.state ? ', ' + address.state : '') +
      ', ' +
      (address.zip ? address.zip : '');

    return hivAddress;
  }

  getHIVAddressOwner() {
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;
    const address = this.customer.application.primaryPolicy.insured.address;

    const ownerState = this.customer.application.primaryPolicy.owner
      ? this.customer.application.primaryPolicy.ownerPersonResidentialAddress.state
      : '';

    // States requiring only primary/alternate physician mapping
    const physicianExemptions = ['AZ', 'IA', 'KY', 'ME', 'MO', 'WI', 'KS', 'LA'];

    // States requiring no mapping for state health address
    const stateHealthExemptions = ['NH', 'PA', 'RI', 'OR', 'UT', 'VT', 'VA', 'WV', 'WA', 'FL', 'DC', 'CA'];

    // States where HIV form can be sent to insured
    const insuredInclusion = ['RI', 'VA'];

    let hivAddress;

    if (hivPosAddy === 'pp_address') {
      hivAddress = this.customer.application.primaryPolicy.insured.personalHistory.getPrimaryPhysicianAddress();
    } else if (
      hivPosAddy === 'pp_another_address' ||
      hivPosAddy === 'insured_another_address' ||
      hivPosAddy === 'other_healthcare'
    ) {
      hivAddress = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveAddress;
    } else if (hivPosAddy === 'insured_address') {
      hivAddress =
        (address.street ? address.street + ', ' : '') +
        (address.line1 ? address.line1 + ', ' : '') +
        (address.city ? address.city : '') +
        (address.state ? ', ' + address.state : '') +
        ', ' +
        (address.zip ? address.zip : '');
    } else {
      hivAddress = 'State Department of Health';
    }

    // Exemptions
    if (physicianExemptions.includes(ownerState) && !['pp_address', 'pp_another_address'].includes(hivPosAddy)) {
      hivAddress = '';
    } else if (stateHealthExemptions.includes(ownerState) && hivPosAddy === 'state_health') {
      hivAddress = '';
    } else if (
      !insuredInclusion.includes(ownerState) &&
      ['insured_address', 'insured_another_address'].includes(hivPosAddy)
    ) {
      hivAddress = '';
    }

    return hivAddress;
  }

  /**
   * Returns lines 1 and 2 from the following
   * type of addresses provided by the
   * 'getHIVAddress' function.
   *
   * 111 E 65 St, Suite 100, New York, NY, 10069
   * 111 E 65 St, New York, NY, 10069
   *
   * @returns {string} Address line 1 and 2, or empty
   * if address format doesn't match.
   */
  getHIVStreet(): string {
    const lines = this.getHIVAddress().split(',');

    if (lines.length === 5) {
      return `${lines[0]} ${lines[1]}`.trim();
    }

    if (lines.length === 4) {
      return lines[0];
    }

    return '';
  }

  /**
   * Returns city from the following
   * type of addresses provided by the
   * 'getHIVAddress' function.
   *
   * 111 E 65 St, Suite 100, New York, NY, 10069
   * 111 E 65 St, New York, NY, 10069
   * New York, NY
   *
   * @returns {string} Address city, or empty
   * if address format doesn't match.
   */
  getHIVCity(): string {
    const lines = this.getHIVAddress().split(',');

    // Covers the case when address is 'pp_address'
    // where just city and state information are collected.
    if (lines.length === 2) {
      return lines[0].trim();
    }

    if (lines.length === 5) {
      return lines[2].trim();
    }

    if (lines.length === 4) {
      return lines[1].trim();
    }

    return '';
  }

  /**
   * Returns state from the following
   * type of addresses provided by the
   * 'getHIVAddress' function.
   *
   * 111 E 65 St, Suite 100, New York, NY, 10069
   * 111 E 65 St, New York, NY, 10069
   * New York, NY
   *
   * @returns {string} Address state, or empty
   * if address format doesn't match.
   */
  getHIVState(): string {
    const lines = this.getHIVAddress().split(',');

    // Covers the case when address is 'pp_address'
    // where just city and state information are collected.
    if (lines.length === 2) {
      return lines[1].trim();
    }

    if (lines.length === 5) {
      return lines[3].trim();
    }

    if (lines.length === 4) {
      return lines[2].trim();
    }

    return '';
  }

  /**
   * Returns zip code from the following
   * type of addresses provided by the
   * 'getHIVAddress' function.
   *
   * 111 E 65 St, Suite 100, New York, NY, 10069
   * 111 E 65 St, New York, NY, 10069
   *
   * @returns {string} Address zip code, or empty
   * if address format doesn't match.
   */
  getHIVZip(): string {
    const lines = this.getHIVAddress().split(',');

    if (lines.length === 5) {
      return lines[4].trim();
    }

    if (lines.length === 4) {
      return lines[3].trim();
    }

    return '';
  }

  getHIVAddress_CityStZip() {
    return this.getHIVCity() + ' ' + this.getHIVState() + ' ' + this.getHIVZip();
  }

  getShowDrHIVInfo() {
    const hivPosAddy = this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress;

    const state = this.customer.application.primaryPolicy.insured.address.state;

    return (
      hivPosAddy === 'pp_address' ||
      hivPosAddy === 'pp_another_address' ||
      hivPosAddy === 'other_healthcare' ||
      hivPosAddy === 'other' ||
      (state === 'VT' &&
        (hivPosAddy === 'insured_address' || hivPosAddy === 'state_health' || hivPosAddy === 'insured_another_address'))
    );
  }

  getHIVConsentDrNameNoStateHealth() {
    const drName = this.getHIVConsentDrName();

    if (this.getShowDrHIVInfo()) {
      return drName === 'Your State Department of Health' ? '' : drName;
    }
    return '';
  }

  getHIVConsentDrNameAndAddress() {
    const self = this;
    let hivDoctorNameAndAddress;
    const drName = this.getHIVConsentDrName();
    let hivAddress = this.getHIVAddress();
    const statesThatNeedOneLine = ['MT'];

    // Should results be sent to primary or other physician
    function reportToPhysician() {
      return (
        self.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_address' ||
        self.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_another_address'
      );
    }

    if (
      !reportToPhysician() &&
      !statesThatNeedOneLine.includes(this.customer.application.primaryPolicy.insured.address.state)
    ) {
      return '';
    }

    if (
      statesThatNeedOneLine.includes(this.customer.application.primaryPolicy.insured.address.state) &&
      self.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'state_health'
    ) {
      hivDoctorNameAndAddress = drName;
    } else if (statesThatNeedOneLine.includes(this.customer.application.primaryPolicy.insured.address.state)) {
      hivDoctorNameAndAddress = drName + ',' + this.getHIVAddress();
    } else {
      hivAddress = hivAddress.split(',');
      if (hivAddress.length === 5) {
        // User correctly entered physician's address with line1 and line2 present
        //TODO: Jermaine check this change
        hivDoctorNameAndAddress =
          drName +
          '\n' +
          hivAddress[0] +
          ' ' +
          hivAddress[1] +
          '\n' +
          hivAddress[2] +
          ',' +
          hivAddress[3] +
          ' ' +
          hivAddress[4];
      } else if (hivAddress.length === 4) {
        //TODO: Jermaine check this change
        // User entered physician's address with only line1 present
        hivDoctorNameAndAddress =
          drName + '\n' + hivAddress[0] + '\n' + hivAddress[1] + ',' + hivAddress[2] + ' ' + hivAddress[3];
      } else {
        // Address is primary physician's address
        hivDoctorNameAndAddress = drName + '\n' + hivAddress[0] + ', ' + hivAddress[1];
      }
    }
    return hivDoctorNameAndAddress;
  }

  getHIVConsentStateDeptAndAddress() {
    // Should results be sent to the state health department primary
    function reportToStateHealthDept() {
      return self.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'state_health';
    }

    const self = this;

    return reportToStateHealthDept() ? 'Your State Department of Health' : '';
  }

  getHIVConsentStateDeptAndAddressTerm2() {
    function reportToStateHealthDept() {
      return self.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'state_health';
    }

    function reportToLocalHealthDept() {
      return self.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'local_health';
    }

    const self = this;

    return reportToStateHealthDept()
      ? 'Your State Department of Health'
      : reportToLocalHealthDept()
      ? 'Your Local Department of Health'
      : '';
  }

  getHIVConsentDrAddress() {
    const address = this.getHIVAddress();

    if (this.getShowDrHIVInfo()) {
      const lines = address.split(',');

      if (lines.length >= 5) {
        return lines[0].concat(lines[1]);
      } else if (lines.length >= 4) {
        return lines[0];
      } else if (lines.length === 1) {
        return address;
      } else {
        return '';
      }
    }

    return '';
  }

  getHIVConsentDrStreet() {
    if (this.getShowDrHIVInfo()) {
      let street;
      const lines = this.getHIVAddress().split(',');

      if (lines.length === 5) {
        // User corrected entered physician's address with line1 and line2
        // @TODO: Jermaine check this change
        street = lines[0] + ' ' + lines[1];
      } else if (lines.length === 4) {
        // User entered physician's address with line1 only
        street = lines[0];
      } else {
        // Primary physician's address is chosen
        street = '';
      }

      return street;
    }

    return '';
  }

  getHIVConsentDrCity() {
    if (this.getShowDrHIVInfo()) {
      const address = this.getHIVAddress();
      const lines = address.split(',');

      if (lines.length >= 3) {
        return lines[lines.length - 3].trim();
      } else if (lines.length >= 2) {
        return lines[lines.length - 2].trim();
      } else {
        return '';
      }
    }

    return '';
  }

  getHIVConsentDrState() {
    if (this.getShowDrHIVInfo()) {
      const address = this.getHIVAddress();
      const lines = address.split(',');

      if (lines.length >= 3) {
        return lines[lines.length - 2].trim();
      } else if (lines.length >= 2) {
        return lines[lines.length - 1].trim();
      } else {
        return '';
      }
    }

    return '';
  }

  getHIVConsentDrZip() {
    if (this.getShowDrHIVInfo()) {
      const address = this.getHIVAddress();
      const lines = address.split(',');

      if (lines.length >= 3) {
        return lines[lines.length - 1].trim();
      }
    }

    return '';
  }

  getHIVConsentDrAddressOrCityState() {
    const address = this.getHIVAddress();
    const lines = address.split(',');

    // Need specific case for PA (so we don't affect any other state by accident)
    if (lines.length > 1 && this.customer.application.primaryPolicy.insured.address.state === 'PA') {
      return lines[0] + ', ' + lines[1] + (lines[2] ? ', ' + lines[2] : '');
    }

    if (lines.length >= 4) {
      return lines[0] + (lines.length >= 5 ? ',' + lines[1] : '');
    }

    return address;
  }

  isHIVResultReleasedToInsured() {
    if (
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'insured_address' ||
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress ===
        'insured_another_address'
    ) {
      return 'Yes';
    }
  }

  isHIVResultReleasedToPhysician() {
    if (
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_address' ||
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_another_address'
    ) {
      return 'Yes';
    }

    return '';
  }

  getSignatureIfReleasedToPhysician(): string {
    if (this.isHIVResultReleasedToPhysician()) {
      return this.customer.application.primaryPolicy.getInsuredFullNameAfterSignature();
    }

    return '';
  }

  getAddressIfReleasedToInsured(): string {
    return this.isHIVResultReleasedToInsured()
      ? this.customer.application.primaryPolicy.insured.address.getOneLineValue()
      : '';
  }

  isHIVResultReleasedToInsuredAndPhysician() {
    if (
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'insured_address' ||
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress ===
        'insured_another_address'
    ) {
      return 'AltSite';
    } else if (
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_address' ||
      this.customer.application.primaryPolicy.insured.personalHistory.hivPositiveWhichAddress === 'pp_another_address'
    ) {
      return 'Physician';
    }

    return '';
  }

  getTLIRIssuingCo() {
    return 'MM';
  }

  getHavenProducerName() {
    return 'Haven Life Insurance Agency, LLC';
  }

  getMedicalSectionObjectsList(): [
    GQLCancer[],
    GQLMedicalCondition[],
    GQLMedicalTreatment[],
    GQLMedicalPrevention[],
    GQLSubstanceUsage[]
  ] {
    const gqlCancers: GQLCancer[] = [];
    const gqlMedicalConditions: GQLMedicalCondition[] = [];
    const gqlMedicalTreatments: GQLMedicalTreatment[] = [];
    const gqlMedicalPrevention: GQLMedicalPrevention[] = [];
    const gqlSubstanceUsages: GQLSubstanceUsage[] = [];

    if (this.bloodTransfusionTreatments && this.bloodTransfusionTreatments.length > 0) {
      this.bloodTransfusionTreatments.forEach((treatment) => {
        treatment.treatmentAmt = this.bloodTransfusionCondition.numberEpisodesTotal;
      });
    }

    if (this.medicalConditions) {
      this.medicalConditions = this.medicalConditions.filter(
        (medicalConditionObj) => medicalConditionObj && medicalConditionObj.conditionType
      );

      this.medicalConditions.forEach((condition, index) => {
        if (condition.conditionType === ConditionType.OLI_MEDCOND_CANCER) {
          if (this.hadCancer) {
            gqlCancers.push(this.mapCancerProperties(condition));
          }
        } else if (
          condition.conditionType === MedicalConditionType.MISCELLANEOUS_CONDITION ||
          condition.conditionType === MedicalConditionType.BLOOD_TRANSFUSION_CONDITION
        ) {
          if (condition.medicalTreatments) {
            condition.medicalTreatments.forEach((treatment) => {
              if (treatment.optionSelected) {
                gqlMedicalTreatments.push(treatment as GQLMedicalTreatment);
              }
            });
          }
          if (condition.medicalPreventions) {
            condition.medicalPreventions.forEach((prevention) => {
              if (prevention.optionSelected) {
                gqlMedicalPrevention.push(prevention as GQLMedicalPrevention);
              }
            });
          }
        } else {
          if (this.isSubstanceAbuseType(condition) && condition.optionSelected === true) {
            gqlSubstanceUsages.push(this.mapSubstanceUsageProperties(condition));
          }
          const medicalCondition = condition;
          if (medicalCondition.medicalTreatments && condition.optionSelected) {
            medicalCondition.medicalTreatments = medicalCondition.medicalTreatments.filter(
              (medicalTreatment) => medicalTreatment && medicalTreatment.optionSelected === true
            );
          }
          if (medicalCondition.medicalPreventions && condition.optionSelected) {
            medicalCondition.medicalPreventions = medicalCondition.medicalPreventions.filter(
              (medicalPrevention) => medicalPrevention && medicalPrevention.optionSelected === true
            );
          }
          const gqlCondition = medicalCondition as unknown as GQLMedicalCondition;
          if (gqlCondition.conditionType && condition.optionSelected && !this.isSubstanceAbuseType(condition)) {
            gqlMedicalConditions.push(gqlCondition);
          }
        }
      });
    }
    return [gqlCancers, gqlMedicalConditions, gqlMedicalTreatments, gqlMedicalPrevention, gqlSubstanceUsages];
  }

  // no separate Cancer record on HIT side, values are mapped from MedicalCondition
  private mapCancerProperties(condition: MedicalCondition): GQLCancer {
    const cancer: GQLCancer = {};
    cancer.medicalTreatments = condition.medicalTreatments;
    cancer.cancerType = condition.subConditionType as CancerType;
    cancer.cancerTypeDesc = condition.conditionDescription;
    cancer.cancerSpreadDesc = condition.conditionSpreadDesc;
    cancer.cancerSpreadInd = condition.conditionSpreadInd;
    cancer.cancerStage = condition.conditionStage;
    cancer.details = condition.details;
    cancer.numOccurrences = condition.numberEpisodesTotal;
    return cancer;
  }

  // no separate SubstanceUsage record on HIT side, values are mapped from MedicalCondition
  private mapSubstanceUsageProperties(condition: MedicalCondition): GQLSubstanceUsage {
    const substanceUsage: GQLSubstanceUsage = {};
    substanceUsage.substanceType = condition.conditionType as SubstanceType;
    substanceUsage.substanceDesc = condition.conditionDescription;
    substanceUsage.substanceLastUseDate = condition.lastEpisodeDate;
    substanceUsage.currentUsageInd = condition.episodeInd;
    substanceUsage.relapseInd = condition.symptomsInd;
    substanceUsage.lastRelapseDate = condition.firstDiagnosisDate;
    const medicalTreatments = condition.medicalTreatments.filter(
      (medicalTreatment) => medicalTreatment && medicalTreatment.optionSelected === true
    );
    substanceUsage.medicalTreatments = medicalTreatments;

    return substanceUsage;
  }

  private isSubstanceAbuseType(condition: MedicalCondition) {
    return (
      condition.conditionType === SubstanceType.MM_SUBSTANCE_DRUGS ||
      condition.conditionType === SubstanceType.OLI_SUBSTANCE_ALCOHOL ||
      condition.conditionType === SubstanceType.MM_SUBSTANCE_TOBACCO ||
      condition.conditionType === SubstanceType.OLI_SUBSTANCE_CANNIBIS
    );
  }

  private setMedicalConditiontoDefault(condition: MedicalCondition): MedicalCondition {
    condition.medicalPreventions = [];
    condition.medicalTreatments = [];
    condition.conditionType = null;
    condition.subConditionType = null;

    return condition;
  }

  @remote({ on: 'server' })
  private setMedicalConditiontoDefaultOnServer(condition: MedicalCondition): MedicalCondition {
    condition.medicalPreventions = [];
    condition.medicalTreatments = [];
    condition.conditionType = null;
    condition.subConditionType = null;

    return condition;
  }
}
